/* Canvas Classics — 2026 Redesign · Theme JS */
(function() {
  'use strict';

  // Shopify CDN URLs may or may not already carry a width param: the gallery pattern URL does,
  // a raw variant featured_image src does not. Replacing one is therefore not enough. Without
  // the append branch a 72px thumbnail downloads the full-resolution product image.
  function sizedUrl(src, w) {
    if (!src) return src;
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, '$1' + w);
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  // Two Shopify CDN URLs are the same picture when their paths match. The query string only
  // carries the version stamp and the width, so it has to be ignored for this comparison.
  function sameImageFile(a, b) {
    if (!a || !b) return false;
    const path = u => String(u).split('?')[0].replace(/^https?:/, '');
    return path(a) === path(b);
  }

  // Room view geometry.
  //
  // Percentages stay the source of truth (resolution independent, and they transfer unchanged
  // between the inline view and the full-screen one), but a percentage of an arbitrary container
  // lands on a fractional pixel. The artwork's edge is then anti-aliased against the wall behind
  // it, which reads as a pale one-to-two pixel seam down the left and along the bottom. It is
  // worst on a fractional devicePixelRatio (1.37 on the machine this was found on) and on the
  // larger sizes, where the edge is simply longer. So the percentages are resolved to pixels
  // snapped to the device grid, and the half-pixel from translateX(-50%) is removed by
  // computing the left offset outright.
  function snapPx(v) {
    const dpr = window.devicePixelRatio || 1;
    return Math.round(v * dpr) / dpr;
  }

  function placeRoomArt(container, art) {
    if (!container || !art) return;
    const wIn = parseFloat(art.dataset.wIn);
    const hIn = parseFloat(art.dataset.hIn);
    const ppi = parseFloat(art.dataset.ppiPct);
    const leftPct = parseFloat(art.dataset.leftPct);
    const bottomPct = parseFloat(art.dataset.bottomPct);
    const rect = container.getBoundingClientRect();
    const cw = rect.width;
    const ch = rect.height;
    // ch is 0 until the room photo has loaded, since nothing else gives the box a height.
    // The caller re-runs this on that load and on resize.
    if (!wIn || !hIn || !ppi || !cw || !ch) return;
    const w = snapPx(cw * wIn * ppi / 100);
    const h = snapPx(w * hIn / wIn);
    // Snapped against absolute screen coordinates rather than container-relative ones. The
    // container sits at a fractional offset itself, so a correctly sized box positioned with
    // snapped container-relative values still lands off the device grid and the seam comes back.
    // Working in screen space and converting back at the end keeps all four edges on the grid.
    const absLeft = snapPx(rect.left + cw * leftPct / 100 - w / 2);
    const absBottom = snapPx(rect.bottom - ch * bottomPct / 100);
    art.style.width = w + 'px';
    art.style.height = h + 'px';
    // A CSS-drawn frame is a face-wide border on the finished-size box (border-box), so the
    // artwork inside is the finished size minus two faces, same as the gallery slide. The face
    // (--face-in: 4 in Classic, 2 3/8 Regency) comes with the finish's --frame-img rule.
    const artCs = getComputedStyle(art);
    const faceIn = parseFloat(artCs.getPropertyValue('--face-in')) || 4;
    art.style.borderWidth = art.dataset.cssFrameArt ? (w * faceIn / wIn) + 'px' : '';
    // A floater (--gap-in, from the style table) leaves a dark gap between its wall and the
    // canvas: padding inside the border, so the image is drawn smaller and the gap shows the
    // background. Every other style has no gap and this writes nothing.
    const gapIn = art.dataset.cssFrameArt ? parseFloat(artCs.getPropertyValue('--gap-in')) || 0 : 0;
    art.style.padding = gapIn ? (w * gapIn / wIn) + 'px' : '';
    art.style.backgroundColor = gapIn ? '#1a1815' : '';
    art.style.left = (absLeft - rect.left) + 'px';
    art.style.bottom = (rect.bottom - absBottom) + 'px';
    // The shadow the moulding casts onto the canvas. An inset box-shadow on the img paints under
    // the image content and never shows, and an img cannot carry a ::after, so it is a sibling
    // laid over the opening. Not on the strip thumbnail, where it would be a smudge.
    if (container.classList.contains('room-thumb')) return;
    let lip = art.ccLip;
    if (!lip) {
      lip = art.ccLip = document.createElement('span');
      lip.className = 'pdp-room__lip';
      lip.setAttribute('aria-hidden', 'true');
      art.after(lip);
    }
    const face = art.dataset.cssFrameArt ? w * faceIn / wIn : 0;
    // No lip shadow when the style has no lip over the art (--lip:0, a floater).
    lip.hidden = !face || !art.getAttribute('src') || artCs.getPropertyValue('--lip').trim() === '0';
    lip.style.width = (w - 2 * face) + 'px';
    lip.style.height = (h - 2 * face) + 'px';
    lip.style.left = (absLeft - rect.left + face) + 'px';
    lip.style.bottom = (rect.bottom - absBottom + face) + 'px';
  }

  // ===========================================================
  // PDP — Frame configurator + Size selector + Sticky ATC
  // ===========================================================
  const buybox = document.querySelector('[data-buybox]');
  if (buybox) initBuybox(buybox);

  // A finish or style name as a key, the way Liquid's handleize makes it, with underscores:
  // "Gloss Black" -> gloss_black, "Black & Gold" -> black_gold.
  function frameKeyOf(name) {
    return String(name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function initBuybox(box) {
    const product = JSON.parse(box.dataset.product || '{}');
    if (!product.variants) return;

    // The swatch that Liquid marked active carries the two finish facts the state needs:
    // which CSS frame to draw (data-css-frame) and, for a finish that has no variants yet,
    // which real option2 value the cart falls back to (data-cart-frame).
    const activeSwatch = document.querySelector('[data-gallery-thumb].active');
    const state = {
      size: box.dataset.defaultSize || 'Medium',
      sizeKey: (box.dataset.defaultSizeKey || 'medium').toLowerCase(),
      frame: box.dataset.defaultFrame || 'Gold',
      frameKey: frameKeyOf(box.dataset.defaultFrameKey || 'gold'),
      cssFrame: (activeSwatch && activeSwatch.dataset.cssFrame) || null,
      cartFrame: (activeSwatch && activeSwatch.dataset.cartFrame) || null,
      // Frame style (2026-09-29): Classic, or Regency on products that carry a Style option.
      style: box.dataset.defaultStyle || 'Classic',
      variant: null
    };
    // Products built with the Style option have bare Size values and the finished size in
    // variant.overall ("24.75 x 13.75"); older products keep "Small (25 x 19)" in option1.
    const hasStyles = product.variants.some(v => v.option3);
    // Frame stock (Theme settings > Frame stock, through snippets/cc-frame-sold-out):
    // "<style>:all" or "<style>:<finish key>" tokens, e.g. "regency:gloss_black".
    const soldOut = (box.dataset.soldOut || '').split(',').map(s => s.trim()).filter(Boolean);
    function isSoldOut() {
      const s = frameKeyOf(state.style || 'Classic').replace(/_/g, '-');
      return soldOut.includes(s + ':all') || soldOut.includes(s + ':' + state.frameKey);
    }

    const els = {
      sizeCards: box.querySelectorAll('[data-size-card]'),
      frameCards: box.querySelectorAll('[data-frame-card]'),
      galleryMain: document.querySelector('[data-gallery-main-img]'),
      thumbs: document.querySelectorAll('[data-gallery-thumb]'),
      // Swipe gallery (replaced the thumbnail rail, 2026-09-09).
      swipe: document.querySelector('[data-gallery-swipe]'),
      slides: document.querySelectorAll('[data-gallery-slide]'),
      dots: document.querySelector('[data-gallery-dots]'),
      thumbstrip: document.querySelector('[data-gallery-thumbstrip]'),
      prev: document.querySelector('[data-gallery-prev]'),
      next: document.querySelector('[data-gallery-next]'),
      // TWO Add to Cart buttons: the one in the buy box, shown on desktop, and the one in the
      // sticky bar, shown on mobile. Only one is ever on screen. Both are real submits of
      // PdpForm (the sticky one reaches it through form="PdpForm" from outside the form), so
      // neither is a proxy for the other and either can arrive as e.submitter.
      atcButtons: document.querySelectorAll('[data-atc]'),
      expressCheckout: box.querySelector('[data-express-checkout]'),
      priceDisplays: document.querySelectorAll('[data-price-display]'),
      specSize: document.querySelector('[data-spec-size]'),
      roomScale: document.querySelector('[data-room-scale]'),
      specFrame: document.querySelector('[data-spec-frame]'),
      specSku: document.querySelector('[data-spec-sku]'),
      sizeValue: box.querySelector('[data-size-value]'),
      styleOptions: box.querySelectorAll('[data-style-option]'),
      styleValue: box.querySelector('[data-style-value]'),
      frameValue: document.querySelector('[data-frame-value]'),
      stickyVariant: document.querySelector('[data-sticky-atc-variant]'),
      stickyPrice: document.querySelector('[data-sticky-atc-price]'),
      variantInput: box.querySelector('[name="id"]'),
      // Room view: all of these are absent unless a room scene is set on the section.
      roomSlide: document.querySelector('[data-slide-room]'),
      room: document.querySelector('[data-room]'),
      roomBg: document.querySelector('[data-room-bg]'),
      roomArts: document.querySelectorAll('[data-room-art]')
    };

    [els.stickyPrice].concat(Array.from(els.priceDisplays)).forEach(el => {
      if (el && !el.hasAttribute('aria-live')) {
        el.setAttribute('aria-live', 'polite');
        el.setAttribute('aria-atomic', 'true');
      }
    });

    // A renamed finish's swatch names its old Frame value in cartFrame; a product caught halfway
    // through the catalog rename can hold either name, so try the old one, then the swatch's own.
    function findVariant() {
      return findVariantNamed(state.cartFrame) || findVariantNamed(state.frame);
    }

    function findVariantNamed(name) {
      if (!name) return undefined;
      const frame = name.toLowerCase();
      if (hasStyles) {
        const size = state.size.toLowerCase();
        const style = state.style.toLowerCase();
        return product.variants.find(v =>
          (v.option1 || '').toLowerCase().split(' (')[0].trim() === size &&
          (v.option2 || '').toLowerCase() === frame &&
          (v.option3 || 'Classic').toLowerCase() === style);
      }
      return product.variants.find(v => {
        // Normalize underscores to spaces so "dark_bronze" in the variant title matches the
        // "Dark Bronze" frame name (without this, dark bronze never resolves a variant).
        const opts = (v.title || '').toLowerCase().replace(/_/g, ' ');
        return opts.includes(state.size.toLowerCase()) && opts.includes(frame);
      });
    }

    function formatMoney(cents) {
      const amount = cents / 100;
      const formatted = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
      return '$' + formatted;
    }

    function getAtcLabelNode(button) {
      if (!button) return null;
      let label = button.querySelector('[data-atc-label]');
      if (label) return label;
      label = document.createElement('span');
      label.setAttribute('data-atc-label', '');
      const text = button.firstChild && button.firstChild.nodeType === Node.TEXT_NODE ? button.firstChild : null;
      if (text) {
        label.textContent = text.textContent.trim();
        button.replaceChild(label, text);
      } else {
        label.textContent = 'Add to Cart';
        button.insertBefore(label, button.firstChild);
      }
      return label;
    }

    function resolveVariantImage(variant) {
      if (variant && variant.featured_image && variant.featured_image.src) {
        return variant.featured_image.src;
      }
      const galleryPattern = box.dataset.galleryPattern;
      if (galleryPattern && galleryPattern.indexOf('__SIZE__SLOT__FRAME__') !== -1) {
        // Mockup files are named by the CSS frame key with underscores (dark_bronze,
        // regency_black), which is not the finish name once a finish is renamed (Charcoal).
        const fileKey = state.cssFrame ? state.cssFrame.replace(/-/g, '_') : state.frameKey;
        return galleryPattern.replace('__SIZE__SLOT__FRAME__', state.sizeKey + '__' + fileKey);
      }
      return null;
    }

    // ===========================================================
    // True-scale room view
    // The piece on the wall is the same variant photo the gallery already shows, scaled
    // to its finished size in inches against a room calibrated once in
    // docs/HANDOFF_room-view-mockup_2026-08-06.md. Nothing is rendered and nothing is
    // pre-generated, so this costs nothing as the catalogue grows.
    // ===========================================================

    // Fallback ladder only. Real dimensions come off the variant, because they vary by
    // piece. "Impression, Sunrise" is 22/30/38/49 and other products differ.
    const ROOM_WIDTH_IN = { small: 22, medium: 30, large: 38, estate: 49 };
    let roomDims = null;
    // The wall image gets no src until the room view is first opened. loading="lazy" cannot do
    // this job: the element starts inside a display:none box, so Chrome fetches its first few KB
    // for the intrinsic size, defers the rest, and then never resumes even after the box is
    // revealed. The result is an empty room on any visit where that URL is not already cached.
    // Withholding the src instead means zero bytes until it is asked for, then a normal fetch.
    let roomOpened = false;

    // option1 carries the finished size as "Small (22 x 19)". Both numbers are read
    // rather than just the first, because nothing guarantees the pair is written
    // width-first, and getting it backwards on a portrait piece would show the wrong
    // size at the exact moment the feature exists to sell scale. Returned long-edge
    // first; sizeRoomArt picks which one is the width from the photo itself.
    // "24.75 x 13.75" -> "24¾ x 13¾" (snippets/cc-inches.liquid does the same in Liquid).
    function prettyInches(s) {
      return String(s).replace(/\.75/g, '¾').replace(/\.25/g, '¼').replace(/\.5/g, '½');
    }
    // The finished size as shown: the stored overall size, else option1's "(W x H)".
    function variantDimsText(variant) {
      if (!variant) return '';
      if (variant.overall) return variant.overall;
      const m = variant.option1 ? variant.option1.match(/\(([^)]+)\)/) : null;
      return m ? m[1] : '';
    }
    function variantDims(variant) {
      const stored = variant && variant.overall ? (variant.overall.match(/[\d.]+/g) || []).map(parseFloat).filter(n => n > 0) : [];
      if (stored.length >= 2) return [Math.max(stored[0], stored[1]), Math.min(stored[0], stored[1])];
      const paren = variant && variant.option1 ? variant.option1.match(/\(([^)]+)\)/) : null;
      const nums = paren ? (paren[1].match(/[\d.]+/g) || []).map(parseFloat).filter(n => n > 0) : [];
      if (nums.length >= 2) return [Math.max(nums[0], nums[1]), Math.min(nums[0], nums[1])];
      const fallback = ROOM_WIDTH_IN[state.sizeKey];
      return fallback ? [fallback, fallback] : null;
    }

    // Width is a percentage of the container, so the wall and the thumbnail take the identical
    // number and neither needs the image to have loaded.
    //
    // Orientation comes from Liquid (data-room-portrait), NOT from naturalWidth/naturalHeight.
    // These images are lazy and the wall one sits in a display:none box, so Chrome reads their
    // first couple of KB for the intrinsic size and then stops: `load` never fires and
    // `complete` stays false indefinitely. Anything hung off that event silently never runs.
    // The natural-size branch survives only as a fallback for a product with no image record.
    function sizeRoomArt(art) {
      if (!art || !roomDims || !els.room) return;
      const ppi = parseFloat(els.room.dataset.roomPpiPct);
      if (!ppi) return;
      const flag = els.room.dataset.roomPortrait;
      const portrait = flag != null && flag !== ''
        ? flag === '1'
        : !!(art.naturalWidth && art.naturalHeight && art.naturalHeight > art.naturalWidth);
      // Stashed on the element so placeRoomArt, the resize handler and the full-screen copy can
      // all re-derive the geometry without going back through variant state. Dimensions are the
      // advertised finished size, which the height is taken from too: without an explicit height
      // the piece collapses to nothing while each new photo decodes, so every size click would
      // flicker on the one interaction the feature exists for.
      art.dataset.wIn = portrait ? roomDims[1] : roomDims[0];
      art.dataset.hIn = portrait ? roomDims[0] : roomDims[1];
      art.dataset.ppiPct = ppi;
      placeRoomArt(art.closest('.pdp-room, .room-thumb'), art);
    }

    function updateRoom() {
      if (!els.room || !els.roomArts.length) return;
      // DEV preview: with a CSS-drawn finish selected, the wall shows the unframed artwork inside
      // the same border-image frame as slide one instead of the Gold variant mockup.
      // The artwork on slide one is eager now and carries a real src (a srcset picks the
      // rendition, so read the attribute, not currentSrc, and let wallArtUrl size it).
      const bareImg = document.querySelector('[data-css-frame-box] img');
      const bareSrc = bareImg && bareImg.getAttribute('src');
      const cssOn = !!(state.cssFrame && bareSrc);
      const src = cssOn ? bareSrc : resolveVariantImage(state.variant);
      roomDims = variantDims(state.variant);
      if (!src || !roomDims) return;
      els.roomArts.forEach(art => {
        if (cssOn) art.dataset.cssFrameArt = state.cssFrame;
        else delete art.dataset.cssFrameArt;
        // Sizing is a percentage and never needs the image, so it happens either way. Gating it
        // on art.complete was the bug that left every piece unsized.
        sizeRoomArt(art);
        // The wall version waits until the room slide has been scrolled to at least once. The
        // thumbnail miniature does not: it is the invitation to go there, so it has to be
        // right on load.
        const inThumb = !!art.closest('.room-thumb');
        if (!roomOpened && !inThumb) return;
        // The wall piece used to take the raw variant URL, which is the full-resolution
        // original: 534KB to fill a box that measures a couple of hundred CSS px inside the
        // room. sizeRoomArt has already set its width, so ask the CDN for what the box
        // actually needs, on the same 100px step the room photo uses. The floor keeps a
        // small selected size from going soft when the lightbox enlarges it.
        const url = wallArtUrl(art, src);
        if (art.getAttribute('src') !== url) art.src = url;
      });
    }

    // The room photo is held back until it is wanted, so opening the room view always paid for
    // a cold fetch: measured at 217KB and about 400ms, which is the blank wall before the scene
    // appears. Two things fix that without spending bytes on visitors who never open it.
    //
    // First, the width. data-src asks for 1600 regardless of viewport, but the box measures
    // 298 CSS px on a 390px phone and 580 on a 1440px desktop, so 1600 is roughly 2.7x
    // oversized on mobile. Derive the width the way the main gallery img's own sizes attribute
    // does, capped at the container's 660px and rounded to a 100px step so viewports of similar
    // width share one CDN entry.
    //
    // Second, the timing. Warm it on intent rather than on the click. Both paths run the same
    // formula, so the warm fetch is the one the open reuses instead of a second download.
    function artWidth(art) {
      const css = art.getBoundingClientRect().width || 300;
      const need = css * (window.devicePixelRatio || 1);
      return Math.min(1600, Math.max(600, Math.ceil(need / 100) * 100));
    }

    // The gallery has already downloaded this exact painting at whatever width its srcset
    // picked. When that copy is the same file and at least as wide as the wall box needs,
    // point at it: the room then opens with no artwork fetch at all, because the bytes are
    // already in cache and already decoded. Only a variant the gallery has not shown falls
    // through to a sized request of its own.
    function wallArtUrl(art, src) {
      const need = artWidth(art);
      const shown = els.galleryMain && (els.galleryMain.currentSrc || els.galleryMain.src);
      if (shown && sameImageFile(shown, src)) {
        const w = parseInt((shown.match(/[?&]width=(\d+)/) || [])[1], 10);
        if (w && w >= need) return shown;
      }
      return sizedUrl(src, need);
    }

    function roomBgUrl() {
      if (!els.roomBg || !els.roomBg.dataset.src) return null;
      const css = Math.min(window.innerWidth || 660, 660);
      const need = css * (window.devicePixelRatio || 1);
      return sizedUrl(els.roomBg.dataset.src, Math.min(1600, Math.ceil(need / 100) * 100));
    }

    function promoteRoomBg() {
      const url = roomBgUrl();
      if (url && !els.roomBg.getAttribute('src')) els.roomBg.src = url;
    }

    els.roomArts.forEach(art => art.addEventListener('load', () => sizeRoomArt(art)));

    // ===========================================================
    // Swipe gallery. One scroll-snap track; the arrows and dots drive it by scrolling rather
    // than by moving any DOM, so a touch swipe and a dot click end in the same state and the
    // browser owns the animation.
    // ===========================================================
    const slides = Array.from(els.slides);
    let currentSlide = 0;
    // Room composites built into the thumbnail strip. They join els.roomArts once the strip is
    // built, so one updateRoom pass sizes and sources the wall piece and the miniature together.
    const roomThumbArts = [];

    function goToSlide(i, smooth) {
      if (!els.swipe || !slides[i]) return;
      els.swipe.scrollTo({ left: slides[i].offsetLeft - els.swipe.offsetLeft, behavior: smooth === false ? 'auto' : 'smooth' });
    }

    // Whichever navigation the section is set to render. Both are lists of buttons in
    // slide order, so marking the current one is the same loop either way.
    const nav = els.dots || els.thumbstrip;

    function markSlide(i) {
      currentSlide = i;
      if (nav) {
        Array.from(nav.children).forEach((d, n) => {
          d.classList.toggle('is-active', n === i);
          d.setAttribute('aria-current', n === i ? 'true' : 'false');
        });
      }
      if (els.prev) els.prev.disabled = i <= 0;
      if (els.next) els.next.disabled = i >= slides.length - 1;
    }

    if (els.swipe && slides.length > 1) {
      if (nav) {
        const thumbs = nav === els.thumbstrip;
        slides.forEach((slide, i) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = thumbs ? 'gallery-thumb' : 'gallery-dot';
          // The slide's own image alt is the only honest label available, and it is already
          // written per slide in Liquid. The video slide has no img, so it stands for itself
          // through its aria-label and its poster.
          const img = slide.querySelector('img[alt]');
          const vid = slide.querySelector('video[poster]');
          btn.setAttribute('aria-label',
            (img && img.alt) || (vid && vid.getAttribute('aria-label')) || ('Image ' + (i + 1)));
          if (thumbs) {
            if (slide === els.roomSlide && els.roomBg && els.roomBg.dataset.src) {
              // The room slide is a composite built at runtime, so a plain <img> thumbnail can
              // only ever be the empty room: the piece is not in that file. Build the same
              // composite in miniature instead, on the same aspect box and the same placement
              // percentages, so the strip shows the piece hanging on the wall. Without this the
              // one slide that sells scale advertises itself with a photo of a bare wall.
              const wrap = document.createElement('span');
              wrap.className = 'room-thumb';
              wrap.style.setProperty('--room-ar', els.room.style.getPropertyValue('--room-ar') || '1');
              const bg = document.createElement('img');
              bg.className = 'pdp-room__bg';
              bg.alt = '';
              bg.loading = 'lazy';
              bg.decoding = 'async';
              // Its own small rendition. The slide-sized room photo is still withheld until the
              // room slide is reached, so this is the only room bytes a page load spends.
              bg.src = sizedUrl(els.roomBg.dataset.src, 320);
              const art = document.createElement('img');
              art.className = 'pdp-room__art';
              art.setAttribute('data-room-art', '');
              art.alt = '';
              art.decoding = 'async';
              // Same placement percentages as the wall version, read off the markup rather than
              // duplicated, so a settings change moves both.
              const wallArt = els.roomArts[0];
              if (wallArt) {
                art.dataset.leftPct = wallArt.dataset.leftPct;
                art.dataset.bottomPct = wallArt.dataset.bottomPct;
              }
              art.addEventListener('load', () => sizeRoomArt(art));
              bg.addEventListener('load', () => sizeRoomArt(art));
              wrap.appendChild(bg);
              wrap.appendChild(art);
              btn.appendChild(wrap);
              roomThumbArts.push(art);
            } else {
              const src = img ? (img.currentSrc || img.src || img.dataset.src)
                              : (vid ? vid.getAttribute('poster') : null);
              const t = document.createElement('img');
              t.alt = '';
              t.loading = 'lazy';
              t.decoding = 'async';
              // 160px is what the strip renders at on a 2x screen; asking for the full
              // rendition here would make every thumbnail a second requester of a large file.
              if (src) t.src = sizedUrl(src, 160);
              btn.appendChild(t);
            }
            // A word under each thumbnail says what the picture is (shown on phones only,
            // theme.css): dots carried no hint that a to-scale or room view existed (2026-10-20).
            const lab = document.createElement('span');
            lab.className = 'gallery-thumb-label';
            lab.setAttribute('aria-hidden', 'true');
            lab.textContent = slide.matches('.gallery-main') ? 'Framed'
              : slide.matches('.gallery-slide--bare') ? 'Unframed'
              : slide.matches('.gallery-slide--detail') ? 'Detail'
              : slide === els.roomSlide || slide.matches('.gallery-slide--room') ? 'To scale'
              : slide.matches('.gallery-slide--video') ? 'Video'
              : slide.hasAttribute('data-representative') ? 'Craft' : 'Photo';
            btn.appendChild(lab);
          }
          btn.addEventListener('click', () => goToSlide(i));
          nav.appendChild(btn);
        });
      }
      if (els.prev) els.prev.addEventListener('click', () => goToSlide(currentSlide - 1));
      if (els.next) els.next.addEventListener('click', () => goToSlide(currentSlide + 1));

      // Which slide is showing is read off the scroller rather than tracked by hand, so a
      // native swipe, a momentum overshoot and a programmatic scroll all report the same thing.
      let scrollTimer = null;
      els.swipe.addEventListener('scroll', () => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => {
          const mid = els.swipe.scrollLeft + els.swipe.clientWidth / 2;
          let idx = 0;
          slides.forEach((s, i) => {
            const left = s.offsetLeft - els.swipe.offsetLeft;
            if (mid >= left && mid < left + s.offsetWidth) idx = i;
          });
          if (idx !== currentSlide) markSlide(idx);
          if (els.roomSlide && slides[idx] === els.roomSlide) openRoom();
          // Swiping off the video slide stops it, so sound never follows the thumb onto a
          // picture of the artwork.
          slides.forEach((s2, n) => {
            if (n === idx) return;
            const v = s2.querySelector('video');
            if (v && !v.paused) v.pause();
          });
        }, 90);
      }, { passive: true });

      if (roomThumbArts.length) {
        els.roomArts = Array.from(els.roomArts).concat(roomThumbArts);
        updateRoom();
      }
      markSlide(0);
    }

    // The room photo is withheld until the room slide is actually reached, the same deferral the
    // hidden overlay used to give for free. Warming it on intent (hover or the first touch on
    // the track) means the scroll lands on a photo that is already fetching.
    function openRoom() {
      if (roomOpened) return;
      promoteRoomBg();
      roomOpened = true;
      updateRoom();
    }
    // The lightbox lives in its own block and cannot see this closure, but it can reach the room
    // SLIDE, so the slide carries the opener. Without this, stepping to the room in the
    // full-screen view found artwork with no src yet and silently skipped past it.
    if (els.roomSlide) els.roomSlide.ccOpenRoom = openRoom;
    if (els.roomSlide) {
      for (const ev of ['pointerenter', 'touchstart']) {
        els.swipe && els.swipe.addEventListener(ev, promoteRoomBg, { once: true, passive: true });
      }
    }

    // The wall box has no height until the room photo lands, so the first placement attempt is a
    // no-op and this is what actually positions the piece.
    if (els.roomBg) {
      els.roomBg.addEventListener('load', () => {
        const art = els.room && els.room.querySelector('[data-room-art]');
        if (art) placeRoomArt(els.room, art);
      });
    }

    // Pixel placement has to be recomputed whenever the containers change size.
    if (els.roomArts.length) {
      let resizeTimer = null;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => els.roomArts.forEach(sizeRoomArt), 120);
      }, { passive: true });
    }

    // Slide one is drawn (2026-09-11): the unframed artwork inside a CSS border-image frame in
    // the selected finish. All the geometry lives in CSS off two custom properties, --fw and
    // --fh, the finished size in inches: the slide is a size container, the box takes
    // min(100cqw, 100cqh*w/h) and its aspect from the pair, and the moulding is 4 in of that.
    // So this only has to keep the pair in step with the selected size and swap the finish.
    // Liquid seeded both for the first paint, which is why nothing here waits on an image.
    // The wrapper only; the five swatches carry data-css-frame too, and on a product without a
    // bare image (no wrapper rendered) the first match would be the Gold swatch button.
    const cssFrame = document.querySelector('.css-frame[data-css-frame]');
    const cssFrameBox = cssFrame && cssFrame.querySelector('[data-css-frame-box]');
    const cssFrameImg = cssFrameBox && cssFrameBox.querySelector('img');
    function layoutCssFrame() {
      if (!cssFrameBox) return;
      const dims = variantDims(state.variant);
      if (!dims) return;
      // Orientation from the artwork itself once it has dimensions, else the flag Liquid set
      // from the image record, so a size click before the picture decodes still lands right.
      const portrait = cssFrameImg && cssFrameImg.naturalWidth && cssFrameImg.naturalHeight
        ? cssFrameImg.naturalHeight > cssFrameImg.naturalWidth
        : cssFrameBox.dataset.portrait === '1';
      const fw = portrait ? dims[1] : dims[0];
      const fh = portrait ? dims[0] : dims[1];
      cssFrameBox.style.setProperty('--fw', fw);
      cssFrameBox.style.setProperty('--fh', fh);
    }
    // The frame image for a finish is a 9-slice border-image, and a border-image that has not
    // decoded yet paints as the transparent border colour over the dark box: a black bar for as
    // long as the download takes. So a finish is swapped in only once its image is decoded, and
    // the old finish stays up until then. Every finish is warmed once the page is idle, so the
    // usual click is instant; the first click on a cold cache waits the download instead of
    // flashing. The URL comes from the section's inline style through a probe element rather
    // than from a second list here, so the two cannot drift.
    const frameImgReady = {};
    const frameImgKeep = [];   // hold each probe Image: Chrome can collect an off-document one mid-load
    function frameImgUrl(finish) {
      const probe = document.createElement('div');
      probe.className = 'css-frame';
      probe.dataset.frame = finish;
      probe.hidden = true;
      document.body.appendChild(probe);
      const raw = getComputedStyle(probe).getPropertyValue('--frame-img').trim();
      probe.remove();
      const m = raw.match(/url\((.*)\)/);
      return m ? m[1].trim().replace(/^['"]|['"]$/g, '') : '';
    }
    function ensureFrameImg(finish) {
      if (!frameImgReady[finish]) {
        const url = frameImgUrl(finish);
        frameImgReady[finish] = url
          ? new Promise(res => {
              const im = new Image();
              frameImgKeep.push(im);
              // onload only. decode() on an image that is not in the document can be garbage
              // collected with its promise unsettled (Chrome), which left every swap waiting forever.
              im.onload = res;
              im.onerror = res;
              im.src = url;
            })
          : Promise.resolve();
      }
      return frameImgReady[finish];
    }
    // Safari 16 and 17 kept the box's moulding width from the OLD finish after data-frame
    // changed (2026-10-08): --face-in is inherited from the wrapper and the cq-based
    // border-width was never recomputed, so Regency drew with Classic's 4 in face and squeezed
    // the painting to half width on tall pieces. Writing --face-in onto the box itself, as
    // --fw/--fh already are, forces the recalculation.
    function syncFace(wrap, box) {
      if (!wrap || !box) return;
      const face = getComputedStyle(wrap).getPropertyValue('--face-in').trim();
      if (face) box.style.setProperty('--face-in', face);
    }
    window.ccSyncFrameFace = syncFace;
    let cssFrameSwap = 0;
    function updateCssFrame() {
      if (!cssFrame) return;
      const on = !!state.cssFrame;
      cssFrame.hidden = !on;
      if (els.galleryMain) els.galleryMain.style.visibility = on ? 'hidden' : '';
      if (!on) return;
      const finish = state.cssFrame;
      if (cssFrame.dataset.frame === finish) { layoutCssFrame(); return; }
      // The new size waits for the new finish (2026-10-08). Classic and Regency differ in
      // moulding width, so Regency dimensions drawn under the still-showing Classic moulding
      // squeezed the painting until the swap landed (up to the 4 s cap on a cold cache).
      const seq = ++cssFrameSwap;
      // Capped at 4 s so a request that never settles cannot pin the old finish.
      Promise.race([ensureFrameImg(finish), new Promise(res => setTimeout(res, 4000))]).then(() => {
        // A later click wins; do not paint a finish the shopper has already moved past.
        // state.cssFrame is the live selection: Gold, then White pending, then Gold again
        // returns early above without a new seq, and this keeps White from landing late.
        if (seq === cssFrameSwap && state.cssFrame === finish) {
          layoutCssFrame();
          cssFrame.dataset.frame = finish;
          syncFace(cssFrame, cssFrameBox);
        }
      });
    }
    if (cssFrame) {
      const warm = () => {
        Array.from(document.querySelectorAll('[data-css-frame]'))
          .map(t => t.dataset.cssFrame).filter(Boolean)
          .forEach(f => ensureFrameImg(f));
      };
      // Warmed on the first sign of intent (pointer over, touch or focus on a finish or style
      // chip) instead of at idle: about 800 KB of frame tiles that most visitors never use.
      const intent = e => {
        if (!e.target.closest || !e.target.closest('.frame-swatch-btn, .style-option-chip')) return;
        ['pointerover', 'touchstart', 'focusin'].forEach(t => document.removeEventListener(t, intent, true));
        warm();
      };
      ['pointerover', 'touchstart', 'focusin'].forEach(t => document.addEventListener(t, intent, { capture: true, passive: true }));
    }

    function refresh() {
      state.variant = findVariant();
      // No variant for this combination: the form must not keep submitting the previous one.
      // Both Add to Cart buttons read Unavailable until a real combination is picked.
      // Older products without a Style option keep Liquid's id and Shopify's own error.
      if (!state.variant) {
        if (hasStyles) {
          if (els.variantInput) els.variantInput.value = '';
          updateSoldOut();
        }
        return;
      }

      const price = formatMoney(state.variant.price);
      // No Add to Cart label carries a price since 2026-09-09: on both breakpoints the price
      // sits immediately to the button's left. getAtcLabelNode is still needed for the
      // "Adding..." state during the fetch.
      els.priceDisplays.forEach(el => { el.textContent = price; });
      // Finished size, finish and item number down the left column follow the same variant.
      if (els.specSize || els.roomScale) {
        const dt = variantDimsText(state.variant);
        const dims = dt ? prettyInches(dt) + ' in' : (state.variant.option1 || '');
        if (els.specSize) els.specSize.textContent = dims;
        // The room caption states the size the wall piece is actually drawn at, so it has to
        // move with the same click that resizes it.
        if (els.roomScale) els.roomScale.textContent = dims;
      }
      if (els.specFrame) els.specFrame.textContent = state.frame;
      if (els.specSku && state.variant.sku) els.specSku.textContent = state.variant.sku;
      if (els.variantInput) els.variantInput.value = state.variant.id;

      if (els.sizeValue) {
        const dt = variantDimsText(state.variant);
        els.sizeValue.textContent = state.size + (dt ? ' (' + prettyInches(dt) + ')' : '');
      }
      if (els.frameValue) els.frameValue.textContent = state.frame;
      if (els.styleValue) els.styleValue.textContent = state.style;
      if (hasStyles) updateSizeCardDims();

      const imgUrl = resolveVariantImage(state.variant);
      // With a drawn frame on screen the mockup element is hidden, so leave it alone: fetching
      // the mockup there would download a picture nobody sees on every Classic click.
      if (imgUrl && els.galleryMain && !(cssFrame && state.cssFrame)) {
        // Liquid renders this element with a srcset and the browser has already chosen and
        // downloaded the right rendition by the time this runs. Overwriting src with an
        // unsized URL made it fetch the SAME picture a second time at full resolution:
        // 288KB through the srcset plus 509KB through this line, on the LCP element.
        // Only touch it when the file itself actually changes, i.e. on a real size or
        // frame swap, and keep the swap sized rather than raw.
        if (!sameImageFile(els.galleryMain.currentSrc || els.galleryMain.src, imgUrl)) {
          els.galleryMain.removeAttribute('srcset');
          els.galleryMain.src = imgUrl;
        }
      }

      // Unconditional: the room slide is laid out whether or not it is the one on screen, so
      // its geometry has to keep up with the selectors either way.
      updateRoom();
      updateCssFrame();

      if (els.stickyVariant) els.stickyVariant.textContent = state.size + ' · ' +
        (hasStyles && state.style.toLowerCase() !== 'classic' ? state.style + ' ' : '') + state.frame;
      if (els.stickyPrice) els.stickyPrice.textContent = price;
      updateSoldOut();
    }

    // A sold-out Regency finish stays selectable so it can be looked at, but nothing buys it:
    // both Add to Cart buttons read Sold out and Buy with Shop is hidden.
    let atcBlocked = false;
    function updateSoldOut() {
      const missing = hasStyles && !state.variant;
      const out = !missing && soldOut.length > 0 && isSoldOut();
      if (!missing && !out && !atcBlocked) return;
      atcBlocked = missing || out;
      els.atcButtons.forEach(b => {
        b.disabled = atcBlocked;
        const label = getAtcLabelNode(b);
        if (label) label.textContent = missing ? 'Unavailable' : out ? 'Sold out' : 'Add to Cart';
      });
      if (els.expressCheckout) els.expressCheckout.hidden = atcBlocked;
    }

    els.sizeCards.forEach(card => {
      card.addEventListener('click', e => {
        e.preventDefault();
        els.sizeCards.forEach(c => {
          c.classList.remove('selected');
          c.setAttribute('aria-checked', 'false');
        });
        card.classList.add('selected');
        card.setAttribute('aria-checked', 'true');
        state.size = card.dataset.sizeName;
        state.sizeKey = card.dataset.sizeKey.toLowerCase();
        // Picking a size no longer swipes the gallery to the room view (2026-09-09). Hijacking
        // the picture on every size click moved the page out from under whoever was comparing
        // the sizes themselves. The room slide stays a place they choose to go; its thumbnail
        // shows the piece on the wall, which is the invitation.
        refresh();
      });
    });

    // Both option groups are role="radiogroup": give them the keyboard contract that role
    // promises. One tab stop per group (roving tabindex on the checked option), arrow keys
    // move and select, Home/End jump to the ends.
    // Only options on screen count (2026-10-27): the finish group holds both styles' swatches
    // and hides the other style's, and End once selected a hidden Regency finish under Classic.
    // Returns setTabStops so a caller that hides or shows options can recompute the tab stop.
    function wireRadioGroup(options, isChecked) {
      const list = Array.from(options);
      if (!list.length) return function() {};
      const usable = () => list.filter(o => !o.hidden && !o.disabled && o.getAttribute('aria-disabled') !== 'true');
      function setTabStops() {
        const live = usable();
        let anyChecked = false;
        list.forEach(o => { const on = live.includes(o) && isChecked(o); o.tabIndex = on ? 0 : -1; if (on) anyChecked = true; });
        if (!anyChecked && live.length) live[0].tabIndex = 0;
      }
      list.forEach(opt => {
        opt.addEventListener('keydown', e => {
          const live = usable();
          const i = live.indexOf(opt);
          if (i < 0) return;
          let next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = live[(i + 1) % live.length];
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = live[(i - 1 + live.length) % live.length];
          else if (e.key === 'Home') next = live[0];
          else if (e.key === 'End') next = live[live.length - 1];
          if (!next) return;
          e.preventDefault();
          next.click();
          setTabStops();
          next.focus();
        });
        opt.addEventListener('click', setTabStops);
      });
      setTabStops();
      return setTabStops;
    }
    wireRadioGroup(els.sizeCards, o => o.getAttribute('aria-checked') === 'true');
    const finishTabStops = wireRadioGroup(els.thumbs, o => o.getAttribute('aria-checked') === 'true');

    // Each size card shows the finished size for the selected style (Regency is a little
    // smaller than Classic at every size). Any finish of that size and style gives it.
    function updateSizeCardDims() {
      els.sizeCards.forEach(card => {
        const out = card.querySelector('[data-size-dims]');
        const size = (card.dataset.sizeName || '').toLowerCase();
        const v = product.variants.find(x =>
          (x.option1 || '').toLowerCase().split(' (')[0].trim() === size &&
          (x.option3 || 'Classic').toLowerCase() === state.style.toLowerCase());
        const dt = variantDimsText(v);
        if (out) out.textContent = dt ? prettyInches(dt) + ' in' : '';
      });
    }

    // Frame style: show that style's swatches only. Keep the finish when the style has it
    // (Gold, Silver), else move to the style's first swatch.
    els.styleOptions.forEach(opt => {
      opt.addEventListener('click', e => {
        e.preventDefault();
        state.style = opt.dataset.styleName;
        els.styleOptions.forEach(o => {
          const on = o === opt;
          o.classList.toggle('selected', on);
          o.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        let pick = null;
        els.thumbs.forEach(t => {
          const mine = (t.dataset.style || 'Classic') === state.style;
          t.hidden = !mine;
          if (mine && !pick && t.dataset.frameName === state.frame) pick = t;
        });
        if (!pick) pick = Array.from(els.thumbs).find(t => !t.hidden);
        if (pick) {
          state.frame = pick.dataset.frameName;
          state.frameKey = frameKeyOf(pick.dataset.frameKey || '');
          state.cssFrame = pick.dataset.cssFrame || null;
          state.cartFrame = pick.dataset.cartFrame || null;
          els.thumbs.forEach(t => {
            t.classList.toggle('active', t === pick);
            t.setAttribute('aria-checked', t === pick ? 'true' : 'false');
          });
        }
        finishTabStops();
        refresh();
      });
    });
    wireRadioGroup(els.styleOptions, o => o.getAttribute('aria-checked') === 'true');

    // Frame is selected from the flat finish swatches in the buy box. Frame Color is a real
    // product option, so this resolves a real variant; nothing about the gallery changes
    // except which mockup slide one shows.
    els.thumbs.forEach(thumb => {
      thumb.addEventListener('click', e => {
        e.preventDefault();
        // A swatch of the other style is hidden and never part of a real variant here.
        if (thumb.hidden) return;
        state.frame = thumb.dataset.frameName;
        state.frameKey = frameKeyOf(thumb.dataset.frameKey || '');
        // Every finish is drawn in CSS on slide one and the room view. A finish that has no
        // variants yet names the real option2 value its price and cart fall back to.
        state.cssFrame = thumb.dataset.cssFrame || null;
        state.cartFrame = thumb.dataset.cartFrame || null;
        els.thumbs.forEach(t => {
          t.classList.toggle('active', t === thumb);
          t.setAttribute('aria-checked', t === thumb ? 'true' : 'false');
        });
        finishTabStops();
        refresh();
      });
    });

    // ===========================================================
    // Add to cart, over AJAX, so the shopper stays on the page and the cart drawer
    // opens instead of the whole page reloading.
    //
    // This stays a real {% form 'product' %} on purpose: if this script never
    // runs, the native POST to /cart/add still adds the item, and that IS the
    // no-JS fallback. Shopify's Web Pixel emits product_added_to_cart off the
    // cart mutation whichever path is used, so Klaviyo's "Added to Cart"
    // metric (RTjEEM) does not depend on which one this is.
    //
    // Deliberately NOT pushing a _learnq 'Added to Cart' alongside it. That
    // Web Pixel already produces the event; a second copy would double the
    // metric and trigger the live Add To Cart Abandonment flow twice per
    // shopper. Same reason there is no Viewed Product snippet in this theme:
    // the Klaviyo app embed already injects one. See
    // docs/ONSITE_TRACKING_REGRESSION_HANDOFF.md.
    // ===========================================================
    const productForm = box.closest('.product-form');
    if (productForm && window.fetch) {
      productForm.addEventListener('submit', function(e) {
        // The express-checkout button lives inside this same form. Only the real ATC
        // button is ours; it now sits in the sticky bar outside the form and is owned
        // by it through form="PdpForm", which still makes it e.submitter. Browsers
        // without e.submitter fall through and get intercepted, which is the
        // behaviour we want by default.
        if (e.submitter && !e.submitter.hasAttribute('data-atc')) return;
        if (isSoldOut()) { e.preventDefault(); return; }

        const id = els.variantInput && els.variantInput.value;
        // No resolved variant for the visible selection: buy nothing rather than the previous one.
        if (hasStyles && !state.variant) { e.preventDefault(); return; }
        // Older products whose options never matched: let Shopify's own POST answer.
        if (!id) return;

        e.preventDefault();

        const qtyInput = productForm.querySelector('[name="quantity"]');
        const qty = Math.max(1, parseInt(qtyInput && qtyInput.value || '1', 10) || 1);
        // Whichever button was pressed shows the progress; the other is off screen at this
        // width but is disabled too, so a keyboard user cannot submit twice through it.
        const btn = (e.submitter && e.submitter.hasAttribute('data-atc')) ? e.submitter : els.atcButtons[0];
        const label = getAtcLabelNode(btn);
        const prevLabel = label ? label.textContent : '';

        els.atcButtons.forEach(b => { b.disabled = true; });
        if (label) label.textContent = 'Adding...';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: qty }] })
        })
          .then(r => { if (!r.ok) throw new Error('add failed'); })
          // Refresh before opening so the drawer never flashes its old contents. The add is
          // saved by now, so a failed redraw opens the drawer with its notice instead of
          // falling through to the native POST below, which would add the item a second time.
          .then(() => syncCartAfterChange().then(() => openCart(), () => openCart()))
          .catch(() => {
            // Everything is print to order, so there is no sold-out path to handle.
            // Any failure here is the network, so hand back to the native POST. A
            // programmatic submit() does not re-fire this handler, so there is no loop.
            productForm.submit();
          })
          .finally(() => {
            els.atcButtons.forEach(b => { b.disabled = false; });
            if (label) label.textContent = prevLabel;
          });
      });
    }

    refresh();
  }

  // ===========================================================
  // Gallery lightbox — click any slide's image to view it full-screen.
  // Click anywhere, the × button, or Esc closes.
  // ===========================================================
  const zoomImgs = document.querySelectorAll('[data-gallery-slide] img:not([data-room-bg]):not([data-room-art])');
  const roomView = document.querySelector('[data-room]');
  if (zoomImgs.length || roomView) {
    const lightbox = document.createElement('div');
    lightbox.className = 'gallery-lightbox';
    // Two modes. A single image for the mockup and the supplemental shots, and a composite for
    // the room view, which cannot be one image: the piece is positioned on the wall at runtime.
    // Rebuilding it here rather than showing the artwork alone keeps the sense of scale that is
    // the whole reason the room view exists, and the percentages carry over to any size.
    lightbox.innerHTML =
      '<button type="button" class="gallery-lightbox-close" aria-label="Close full-screen view">&times;</button>' +
      '<button type="button" class="gallery-lightbox-nav gallery-lightbox-prev" aria-label="Previous image"></button>' +
      '<button type="button" class="gallery-lightbox-nav gallery-lightbox-next" aria-label="Next image"></button>' +
      '<img class="gallery-lightbox__single" alt="">' +
      '<div class="gallery-lightbox__room" hidden>' +
        '<img class="gallery-lightbox__room-bg" alt="">' +
        '<img class="gallery-lightbox__room-art" alt="">' +
      '</div>' +
      // Third mode, for the drawn slide: the same border-image box as the page, so enlarging
      // the framed piece shows the framed piece rather than the bare artwork underneath it.
      '<div class="gallery-lightbox__framed" hidden>' +
        '<div class="css-frame-box" data-lb-frame-box><img alt=""></div>' +
      '</div>';
    document.body.appendChild(lightbox);
    const single = lightbox.querySelector('.gallery-lightbox__single');
    const roomWrap = lightbox.querySelector('.gallery-lightbox__room');
    const roomBgL = lightbox.querySelector('.gallery-lightbox__room-bg');
    const roomArtL = lightbox.querySelector('.gallery-lightbox__room-art');
    const framedWrap = lightbox.querySelector('.gallery-lightbox__framed');
    const framedBox = framedWrap.querySelector('[data-lb-frame-box]');
    const framedImg = framedBox.querySelector('img');

    const openLightbox = () => {
      lightbox.classList.add('open');
      document.body.style.overflow = 'hidden';
    };
    const closeLightbox = () => {
      lightbox.classList.remove('open');
      document.body.style.overflow = '';
    };

    // Every slide the lightbox can show, in track order: any slide with a real image, plus the
    // room slide, which has no single image of its own. The video slide is deliberately absent,
    // it has its own controls. Built from the DOM rather than from zoomImgs so the order here is
    // the order on the page, which is what makes stepping through it feel like the track.
    const lbSlides = Array.from(document.querySelectorAll('[data-gallery-slide]')).filter(sl =>
      sl.querySelector('[data-room]') ||
      sl.querySelector('img:not([data-room-bg]):not([data-room-art])')
    );
    let lbIndex = 0;

    const showRoomIn = roomEl => {
      const bg = roomEl.querySelector('[data-room-bg]');
      const art = roomEl.querySelector('[data-room-art]');
      if (!bg || !art || !art.getAttribute('src')) return false;
      single.hidden = true;
      framedWrap.hidden = true;
      roomWrap.hidden = false;
      roomBgL.src = bg.currentSrc || bg.src;
      // A larger rendition than the inline view uses, so the piece still resolves detail
      // once the composite fills the screen.
      roomArtL.src = sizedUrl(art.getAttribute('src'), 2048);
      roomArtL.alt = art.alt;
      // Carry the percentages, not the resolved pixels: this composite is a different size, so
      // it re-resolves and re-snaps them against its own box.
      ['wIn', 'hIn', 'ppiPct', 'leftPct', 'bottomPct'].forEach(k => {
        roomArtL.dataset[k] = art.dataset[k];
      });
      // The drawn finish too, or the enlarged room shows the bare artwork with no frame.
      if (art.dataset.cssFrameArt) roomArtL.dataset.cssFrameArt = art.dataset.cssFrameArt;
      else delete roomArtL.dataset.cssFrameArt;
      // The container has to hold the photo's exact proportions or the hang height, being a
      // percentage of HEIGHT, would land in the wrong place.
      const w = bg.naturalWidth || parseFloat(bg.getAttribute('width')) || 1;
      const h = bg.naturalHeight || parseFloat(bg.getAttribute('height')) || 1;
      roomWrap.style.setProperty('--room-ar', (w / h).toFixed(4));
      placeRoomArt(roomWrap, roomArtL);
      return true;
    };

    // The drawn slide: copy the finish and the finished size across to the lightbox's own box,
    // which sizes itself against the viewport by the same CSS the page uses.
    const showFramed = box => {
      const frame = box.closest('[data-css-frame]');
      const img = box.querySelector('img');
      const src = img && (img.currentSrc || img.src);
      if (!frame || frame.hidden || !src) return false;
      single.hidden = true;
      roomWrap.hidden = true;
      framedWrap.hidden = false;
      framedWrap.dataset.frame = frame.dataset.frame;
      framedBox.style.setProperty('--fw', box.style.getPropertyValue('--fw'));
      framedBox.style.setProperty('--fh', box.style.getPropertyValue('--fh'));
      if (window.ccSyncFrameFace) window.ccSyncFrameFace(framedWrap, framedBox);
      framedImg.src = sizedUrl(src, 2048);
      framedImg.alt = img.alt;
      return true;
    };

    const showImage = img => {
      single.hidden = false;
      roomWrap.hidden = true;
      framedWrap.hidden = true;
      // Deliberately NOT currentSrc as-is: the mockup is served from a srcset and the browser
      // will have picked a 600w or 900w rendition for a 660px slot, which then gets blown up to
      // fill the screen and looks soft. Ask for 2048 so the full-screen view is actually worth
      // opening. The print detail crop carries its native width and is never resized.
      single.src = sizedUrl(img.currentSrc || img.src, img.dataset.nativeWidth || 2048);
      single.alt = img.alt;
    };

    // Render slide i without opening or closing anything, so stepping and opening share one path.
    function renderSlide(i) {
      const sl = lbSlides[i];
      if (!sl) return false;
      const room = sl.querySelector('[data-room]');
      if (room) {
        // The room composite needs its artwork promoted first: a visitor can reach the room
        // through the lightbox arrows without ever landing on the room slide itself, and the
        // photo and the piece are both withheld until then.
        if (typeof sl.ccOpenRoom === 'function') sl.ccOpenRoom();
        if (showRoomIn(room)) { lbIndex = i; updateArrows(); return true; }
        return false;
      }
      const fbox = sl.querySelector('[data-css-frame-box]');
      // A drawn frame that is switched off (a Regency finish) is not what the slide shows.
      const fboxOn = fbox && !fbox.closest('[hidden]');
      if (fboxOn && showFramed(fbox)) { lbIndex = i; updateArrows(); return true; }
      const img = (fbox && sl.querySelector('img[data-gallery-main-img]')) ||
        sl.querySelector('img:not([data-room-bg]):not([data-room-art])');
      if (!img) return false;
      showImage(img);
      lbIndex = i;
      updateArrows();
      return true;
    }

    function stepSlide(delta) {
      // Walk past anything that will not render (a room whose artwork has not resolved) rather
      // than dead-ending on it.
      for (let n = lbIndex + delta; n >= 0 && n < lbSlides.length; n += delta) {
        if (renderSlide(n)) return;
      }
    }

    function updateArrows() {
      lbPrev.disabled = lbIndex <= 0;
      lbNext.disabled = lbIndex >= lbSlides.length - 1;
    }

    const lbPrev = lightbox.querySelector('.gallery-lightbox-prev');
    const lbNext = lightbox.querySelector('.gallery-lightbox-next');
    // stopPropagation because the lightbox itself closes on any click.
    lbPrev.addEventListener('click', e => { e.stopPropagation(); stepSlide(-1); });
    lbNext.addEventListener('click', e => { e.stopPropagation(); stepSlide(1); });
    if (lbSlides.length < 2) { lbPrev.hidden = true; lbNext.hidden = true; }

    // Open at whichever slide holds this element, so a click on a picture opens that picture.
    const openFrom = el => {
      const sl = el.closest('[data-gallery-slide]');
      const i = lbSlides.indexOf(sl);
      if (i > -1 && renderSlide(i)) openLightbox();
    };

    zoomImgs.forEach(img => img.addEventListener('click', () => openFrom(img)));

    // Swipe the full-screen view the same way the track is swiped, so enlarging one slide and
    // moving to the next does not mean closing and reopening (2026-09-09). Horizontal only, and
    // only past a threshold, so a tap to close still closes.
    let lbX = 0, lbY = 0, lbMoved = false;
    lightbox.addEventListener('touchstart', e => {
      const t = e.changedTouches[0];
      lbX = t.clientX; lbY = t.clientY; lbMoved = false;
    }, { passive: true });
    lightbox.addEventListener('touchend', e => {
      const t = e.changedTouches[0];
      const dx = t.clientX - lbX, dy = t.clientY - lbY;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        lbMoved = true;
        stepSlide(dx < 0 ? 1 : -1);
      }
    }, { passive: true });

    // The Enlarge chip sits on the mockup slide and opens whatever slide is currently showing,
    // so it keeps working after a swipe rather than being pinned to the first image.
    const zoomChip = document.querySelector('[data-gallery-zoom]');
    if (zoomChip) {
      zoomChip.addEventListener('click', e => {
        e.stopPropagation();
        const track = document.querySelector('[data-gallery-swipe]');
        let slide = null;
        if (track) {
          const mid = track.scrollLeft + track.clientWidth / 2;
          Array.from(track.querySelectorAll('[data-gallery-slide]')).forEach(sl => {
            const left = sl.offsetLeft - track.offsetLeft;
            if (mid >= left && mid < left + sl.offsetWidth) slide = sl;
          });
        }
        const i = slide ? lbSlides.indexOf(slide) : 0;
        // The video slide is not in lbSlides, so the chip falls back to the first openable one
        // rather than doing nothing at all, which is what it used to do there.
        if (renderSlide(i > -1 ? i : 0)) openLightbox();
      });
    }

    if (roomView) {
      roomView.addEventListener('click', () => openFrom(roomView));

      // Keep the full-screen composite correct when the window changes size under it.
      let lbResizeTimer = null;
      window.addEventListener('resize', () => {
        if (!lightbox.classList.contains('open') || roomWrap.hidden) return;
        clearTimeout(lbResizeTimer);
        lbResizeTimer = setTimeout(() => placeRoomArt(roomWrap, roomArtL), 120);
      }, { passive: true });
    }

    lightbox.addEventListener('click', () => { if (!lbMoved) closeLightbox(); lbMoved = false; });
    document.addEventListener('keydown', e => {
      if (!lightbox.classList.contains('open')) return;
      if (e.key === 'Escape') { closeLightbox(); return; }
      // Arrow keys step the full-screen view, matching the arrow keys on the track.
      if (e.key === 'ArrowRight') { e.preventDefault(); stepSlide(1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); stepSlide(-1); }
    });
  }

  // ===========================================================
  // Sticky ATC, bottom-docked. Shown whenever the buy box's own Add to Cart is off screen, at
  // every width (phones too since 2026-10-03, when that button came back on mobile).
  //
  // The bar is position:fixed, so it is out of the flow and would otherwise sit on top of
  // whatever ends the page. Pad the body by its measured height rather than a guessed
  // constant, since the height depends on the title wrapping and on the safe-area inset.
  // ===========================================================
  // The bar appears only once the buy box's own Add to Cart has scrolled out of view, at every
  // width. It was permanently visible for part of 2026-09-09, which put two Add to Cart buttons
  // on screen at once as soon as the buy box grew one of its own. Joshua's call, same day:
  // not immediately, only after they scroll past the real one.
  const stickyAtc = document.querySelector('[data-sticky-atc]');
  const buyboxSection = document.querySelector('[data-buybox-section]');
  const inPageAtc = document.querySelector('.buybox-buy [data-atc]');
  if (stickyAtc && inPageAtc && 'IntersectionObserver' in window) {
    new IntersectionObserver(entries => {
      stickyAtc.classList.toggle('visible', !entries[0].isIntersecting);
    }).observe(inPageAtc);
  } else if (stickyAtc && buyboxSection) {
    // No IntersectionObserver: fall back to the old past-the-buybox scroll check.
    window.addEventListener('scroll', () => {
      const past = buyboxSection.offsetTop + buyboxSection.offsetHeight - 200;
      stickyAtc.classList.toggle('visible', window.scrollY > past);
    }, { passive: true });
  }
  if (stickyAtc) {
    // Reserve the bar's height at the foot of the page. It is showing by the time anyone
    // reaches the bottom, so without this it covers whatever ends the page.
    const padForSticky = () => {
      document.body.style.paddingBottom = stickyAtc.offsetHeight + 'px';
    };
    padForSticky();
    window.addEventListener('load', padForSticky);
    window.addEventListener('resize', padForSticky, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(padForSticky).observe(stickyAtc);
  }

  // ===========================================================
  // Collection filter form — submit on any input change.
  // ===========================================================
  document.querySelectorAll('[data-collection-filters-form]').forEach(form => {
    let priceDebounce;

    function submitFilters() {
      const data = new FormData(form);
      const params = new URLSearchParams();
      for (const [k, v] of data.entries()) {
        if (v !== '' && v != null) params.append(k, v);
      }
      params.delete('page');
      const base = form.getAttribute('action') || window.location.pathname;
      const qs = params.toString();
      window.location.href = qs ? base + '?' + qs : base;
    }

    form.addEventListener('change', e => {
      const t = e.target;
      if (!t) return;
      if (t.matches('[data-filter-input]') || t.matches('[data-sort-select]')) {
        if (t.type === 'number') return;
        submitFilters();
      }
    });

    form.addEventListener('input', e => {
      const t = e.target;
      if (t && t.type === 'number' && t.matches('[data-filter-input]')) {
        clearTimeout(priceDebounce);
        priceDebounce = setTimeout(submitFilters, 700);
      }
    });

    const sortSelect = document.querySelector('[data-sort-select][form="' + form.id + '"]');
    if (sortSelect && !form.contains(sortSelect)) {
      sortSelect.addEventListener('change', submitFilters);
    }
  });

  // ===========================================================
  // Infinite scroll — load next page when sentinel enters viewport.
  //
  // Back-button restore (2026-10-02): Shopify pages are sent no-store, which keeps Chrome from
  // restoring a page from its back/forward cache, so Back from a painting reloaded the
  // collection at batch one and the shopper lost their place. Tapping a card now saves, per
  // tab (sessionStorage), how many batches were loaded and which card was tapped. Coming BACK
  // to the same URL (navigation type back_forward, under 30 minutes) loads those batches again
  // and scrolls to that card. Any other visit starts at the top as before.
  // ===========================================================
  (function() {
    const sentinel = document.querySelector('[data-infinite-scroll]');
    if (!sentinel) return;

    const grid = document.querySelector('.collection-grid');
    if (!grid) return;

    let loading = false;
    let batches = 1;
    const KEY = 'cc-place:' + location.pathname + location.search;

    function finish() {
      sentinel.removeAttribute('data-next-url');
      sentinel.style.display = 'none';
      observer.disconnect();
    }

    // Loads the next batch; resolves true if one was appended.
    function loadNext() {
      const nextUrl = sentinel.getAttribute('data-next-url');
      if (!nextUrl) { finish(); return Promise.resolve(false); }
      loading = true;
      sentinel.classList.add('is-loading');
      return fetch(nextUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newCards = doc.querySelectorAll('.collection-grid > *');
          newCards.forEach(card => grid.appendChild(card));
          batches += 1;
          const newSentinel = doc.querySelector('[data-infinite-scroll]');
          const newNext = newSentinel ? newSentinel.getAttribute('data-next-url') : null;
          if (newNext) sentinel.setAttribute('data-next-url', newNext); else finish();
          return true;
        })
        .catch(() => { finish(); return false; })
        .finally(() => {
          loading = false;
          sentinel.classList.remove('is-loading');
        });
    }

    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (!entry.isIntersecting || loading) return;
      loadNext();
    }, { rootMargin: '400px' });

    // Remember the place when a painting is tapped (not the save heart).
    grid.addEventListener('click', e => {
      if (e.target.closest('[data-save-toggle]') || e.target.closest('[data-rv]')) return;
      const card = e.target.closest('a[href]');
      if (!card) return;
      try {
        sessionStorage.setItem(KEY, JSON.stringify({ batches: batches, href: card.getAttribute('href'), y: window.scrollY, t: Date.now() }));
      } catch (err) { /* storage blocked: Back just starts at the top */ }
    });

    let saved = null;
    try {
      const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      const raw = sessionStorage.getItem(KEY);
      sessionStorage.removeItem(KEY);
      if (raw && nav && nav.type === 'back_forward') {
        saved = JSON.parse(raw);
        if (!saved || Date.now() - saved.t > 30 * 60 * 1000) saved = null;
      }
    } catch (err) { saved = null; }

    if (!saved || saved.batches <= 1) {
      observer.observe(sentinel);
      return;
    }

    // Fetch every batch the shopper had loaded at once, then append in order.
    // One at a time made Back wait a round trip per batch (slow on desktop).
    function restoreBatches(count) {
      const first = sentinel.getAttribute('data-next-url');
      if (!first) return Promise.resolve();
      const base = new URL(first, location.href);
      const start = parseInt(base.searchParams.get('page'), 10) || 2;
      const urls = [];
      for (let p = start; p < start + count; p++) {
        const u = new URL(base);
        u.searchParams.set('page', p);
        urls.push(u.pathname + u.search);
      }
      sentinel.classList.add('is-loading');
      return Promise.all(urls.map(u =>
        fetch(u, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.text()).catch(() => null)
      )).then(pages => {
        let next = first;
        for (const html of pages) {
          if (!html) { next = null; break; }
          const doc = new DOMParser().parseFromString(html, 'text/html');
          doc.querySelectorAll('.collection-grid > *').forEach(card => grid.appendChild(card));
          batches += 1;
          const s = doc.querySelector('[data-infinite-scroll]');
          next = s ? s.getAttribute('data-next-url') : null;
          if (!next) break;
        }
        sentinel.classList.remove('is-loading');
        if (next) sentinel.setAttribute('data-next-url', next); else finish();
      });
    }

    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    restoreBatches(saved.batches - 1).then(() => {
      const target = Array.prototype.find.call(grid.querySelectorAll('a[href]:not([data-rv-link])'), a => a.getAttribute('href') === saved.href);
      if (target) target.scrollIntoView({ block: 'center' });
      else window.scrollTo(0, saved.y);
      if (sentinel.getAttribute('data-next-url')) observer.observe(sentinel);
    });
  })();

  // ===========================================================
  // Mobile filter drawer — open/close the filter rail on small screens.
  //
  // The CSS is keyed off body.filters-mobile-open (NOT a class on the aside
  // or the backdrop). Inside @media(max-width:900px):
  //   body.filters-mobile-open .collection-filters    → transform: translateX(0)
  //   body.filters-mobile-open                        → overflow: hidden (lock scroll)
  // and unconditionally:
  //   body.filters-mobile-open .filters-mobile-backdrop → opacity/visibility/pointer-events on
  // ===========================================================
  const filtersAside = document.querySelector('[data-collection-filters]');
  const filtersTrigger = document.querySelector('[data-filters-trigger]');
  const filtersClose = document.querySelector('[data-filters-close]');
  const filtersBackdrop = document.querySelector('[data-filters-backdrop]');
  if (filtersAside && filtersTrigger) {
    // Below 900px the panel slides off screen when closed, but its checkboxes stayed in the
    // Tab order, so keyboard users walked through invisible filters (2026-10-26 audit). Closed
    // on a phone it is inert; open, focus moves in and Tab stays inside until it closes; at
    // desktop widths it is an ordinary sidebar again.
    const filtersMq = window.matchMedia('(max-width: 900px)');
    const filtersOpen = () => document.body.classList.contains('filters-mobile-open');
    const syncFiltersInert = () => { filtersAside.inert = filtersMq.matches && !filtersOpen(); };
    // Reachable by Tab: laid out, not visibility:hidden, and not inside a closed group (a
    // closed <details> keeps its own summary reachable but nothing else).
    const filterFocusables = () => Array.prototype.filter.call(
      filtersAside.querySelectorAll('a[href],summary,button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'),
      el => (el.offsetWidth > 0 || el.offsetHeight > 0) && getComputedStyle(el).visibility !== 'hidden' &&
        !(el.tagName !== 'SUMMARY' && el.closest('details:not([open])')) &&
        !(el.tagName === 'SUMMARY' && el.parentElement.parentElement && el.parentElement.parentElement.closest('details:not([open])'))
    );
    // On phones the toolbar trigger is hidden and the quick-filter chips open the panel, so
    // focus goes back to whatever opened it.
    let filtersReturn = null;
    const openFilters = () => {
      filtersReturn = document.activeElement;
      document.body.classList.add('filters-mobile-open');
      filtersTrigger.setAttribute('aria-expanded', 'true');
      if (filtersBackdrop) filtersBackdrop.setAttribute('aria-hidden', 'false');
      syncFiltersInert();
      const first = filtersClose || filterFocusables()[0];
      if (first) setTimeout(() => first.focus(), 60);
    };
    const closeFilters = () => {
      const wasOpen = filtersOpen();
      document.body.classList.remove('filters-mobile-open');
      filtersTrigger.setAttribute('aria-expanded', 'false');
      if (filtersBackdrop) filtersBackdrop.setAttribute('aria-hidden', 'true');
      syncFiltersInert();
      if (wasOpen && filtersMq.matches) {
        const shown = el => el && el !== document.body && el.isConnected && el.offsetWidth > 0;
        const back = [filtersReturn, filtersTrigger, document.querySelector('[data-qf-open]')].find(shown);
        if (back) back.focus();
      }
      filtersReturn = null;
    };
    filtersTrigger.addEventListener('click', openFilters);
    if (filtersClose) filtersClose.addEventListener('click', closeFilters);
    if (filtersBackdrop) filtersBackdrop.addEventListener('click', closeFilters);
    document.addEventListener('keydown', e => {
      if (!filtersOpen() || !filtersMq.matches) return;
      if (e.key === 'Escape') { closeFilters(); return; }
      if (e.key !== 'Tab') return;
      const items = filterFocusables();
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (!filtersAside.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    const onFiltersMq = () => {
      if (!filtersMq.matches && filtersOpen()) closeFilters();
      syncFiltersInert();
    };
    if (filtersMq.addEventListener) filtersMq.addEventListener('change', onFiltersMq);
    else if (filtersMq.addListener) filtersMq.addListener(onFiltersMq);
    syncFiltersInert();
  }

  // ===========================================================
  // Mobile NAV drawer — hamburger menu (small screens).
  //
  // The CSS is keyed off body.drawer-is-open (NOT body.drawer-open or a class
  // on the drawer element). Specifically:
  //   body.drawer-is-open .drawer        → transform: translate(0), visibility: visible
  //   body.drawer-is-open .drawer-backdrop → opacity: 1, visibility: visible
  //   body.drawer-is-open .hamburger-bars → animates into X
  //   body.drawer-is-open                 → overflow: hidden (lock scroll)
  // ===========================================================
  // Mega-menu featured images ship as data-src (~490KB across the three menus) and only
  // load on the first sign of nav intent: hover, touch, or keyboard focus anywhere in the
  // nav. That beats the dropdown opening by enough to have the image ready, and visitors
  // who never touch the nav never pay for it. Same warm-on-intent pattern as the room view.
  const primaryNav = document.querySelector('.nav-left');
  if (primaryNav) {
    const promoteMenuImages = () => {
      for (const img of document.querySelectorAll('.mega-menu-featured-img[data-src]')) {
        // srcset first: with it already in place the src assignment resolves against the
        // candidate list rather than fetching the fallback and then re-picking.
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    };
    for (const ev of ['pointerenter', 'touchstart', 'focusin']) {
      primaryNav.addEventListener(ev, promoteMenuImages, { once: true, passive: true });
    }
  }

  const navDrawer = document.querySelector('[data-drawer]');
  const navDrawerBackdrop = document.querySelector('[data-drawer-backdrop]');

  function openNavDrawer() {
    document.body.classList.add('drawer-is-open');
    if (navDrawer) { navDrawer.setAttribute('aria-hidden', 'false'); navDrawer.inert = false; }
    if (navDrawerBackdrop) navDrawerBackdrop.setAttribute('aria-hidden', 'false');
    const trigger = document.querySelector('[data-drawer-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  function closeNavDrawer() {
    document.body.classList.remove('drawer-is-open');
    if (navDrawer) { navDrawer.setAttribute('aria-hidden', 'true'); navDrawer.inert = true; }
    if (navDrawerBackdrop) navDrawerBackdrop.setAttribute('aria-hidden', 'true');
    const trigger = document.querySelector('[data-drawer-open]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  document.addEventListener('click', function(e) {
    const openTrigger = e.target.closest('[data-drawer-open]');
    const closeTrigger = e.target.closest('[data-drawer-close]');
    const softClose = e.target.closest('[data-drawer-close-soft]');
    const accordionToggle = e.target.closest('[data-drawer-toggle]');

    if (openTrigger) {
      e.preventDefault();
      openNavDrawer();
      return;
    }
    if (closeTrigger) {
      e.preventDefault();
      closeNavDrawer();
      return;
    }
    if (accordionToggle) {
      e.preventDefault();
      const item = accordionToggle.closest('[data-drawer-item]');
      if (item) {
        const wasExpanded = accordionToggle.getAttribute('aria-expanded') === 'true';
        item.parentElement.querySelectorAll('[data-drawer-item].is-open').forEach(sib => {
          if (sib !== item) {
            sib.classList.remove('is-open');
            const btn = sib.querySelector('[data-drawer-toggle]');
            if (btn) btn.setAttribute('aria-expanded', 'false');
          }
        });
        item.classList.toggle('is-open', !wasExpanded);
        accordionToggle.setAttribute('aria-expanded', wasExpanded ? 'false' : 'true');
      }
      return;
    }
    if (softClose) {
      closeNavDrawer();
    }
  });

  if (navDrawerBackdrop) {
    navDrawerBackdrop.addEventListener('click', closeNavDrawer);
  }

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && document.body.classList.contains('drawer-is-open')) {
      closeNavDrawer();
    }
  });

  // ===========================================================
  // Cart drawer + cart page — open/close, qty, remove, AJAX update.
  // ===========================================================
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartBackdrop = document.querySelector('[data-cart-backdrop]');

  // The drawer declares role="dialog" aria-modal="true", so assistive tech is
  // told a modal opened. Without moving focus into it that promise is broken:
  // the keyboard user is left on the page behind, tabbing through content a
  // screen reader has just been told is inert.
  let cartLastFocus = null;
  // iOS Safari ignores overflow:hidden on <body>, so the page behind the drawer
  // keeps scrolling and the drawer scroll-chains into it. Pinning the body at a
  // negative offset is the only reliable lock; the offset is restored on close.
  let cartScrollY = 0;

  const CART_FOCUSABLE = 'a[href],summary,button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function cartFocusables() {
    if (!cartDrawer) return [];
    return Array.prototype.filter.call(
      cartDrawer.querySelectorAll(CART_FOCUSABLE),
      el => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement
    );
  }

  function openCart() {
    if (cartDrawer && cartDrawer.classList.contains('is-open')) return;
    cartLastFocus = document.activeElement;
    cartScrollY = window.scrollY || window.pageYOffset || 0;
    if (cartDrawer) {
      cartDrawer.classList.add('is-open');
      cartDrawer.setAttribute('aria-hidden', 'false');
      cartDrawer.inert = false;
    }
    if (cartBackdrop) {
      cartBackdrop.classList.add('is-visible');
      cartBackdrop.setAttribute('aria-hidden', 'false');
    }
    document.body.classList.add('cart-open');
    document.body.style.position = 'fixed';
    document.body.style.top = (-cartScrollY) + 'px';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    // Focus the close button rather than the first line item link, so a screen
    // reader user lands on a way out instead of hearing the whole cart first.
    const closeBtn = cartDrawer && cartDrawer.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.focus();
    syncDrawerOnOpen();
  }

  function closeCart() {
    if (cartDrawer) {
      cartDrawer.classList.remove('is-open');
      cartDrawer.setAttribute('aria-hidden', 'true');
      cartDrawer.inert = true;
    }
    if (cartBackdrop) {
      cartBackdrop.classList.remove('is-visible');
      cartBackdrop.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('cart-open');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, cartScrollY);
    // Return focus where it came from, or the shopper is dumped at the top of
    // the document with no idea where they were.
    if (cartLastFocus && document.contains(cartLastFocus) && typeof cartLastFocus.focus === 'function') {
      cartLastFocus.focus();
    }
    cartLastFocus = null;
  }

  document.addEventListener('click', function(e) {
    const openTrigger = e.target.closest('[data-cart-open]');
    const closeTrigger = e.target.closest('[data-cart-close]');
    const softClose = e.target.closest('[data-cart-close-soft]');
    if (openTrigger) {
      e.preventDefault();
      openCart();
    } else if (closeTrigger) {
      e.preventDefault();
      closeCart();
    } else if (softClose) {
      closeCart();
    }
  });
  if (cartBackdrop) cartBackdrop.addEventListener('click', closeCart);

  document.addEventListener('keydown', function(e) {
    if (!cartDrawer || !cartDrawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeCart(); return; }
    if (e.key !== 'Tab') return;
    // Trap Tab inside the drawer. Without this, tabbing walks straight out of an
    // aria-modal dialog into the page behind it, which is still fully rendered.
    const items = cartFocusables();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (!cartDrawer.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  const isCartPage = /^\/cart(\/?|\?.*)?$/.test(location.pathname + location.search);

  function setCartCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      if (n > 0) {
        el.textContent = n;
        el.style.display = '';
      } else {
        el.textContent = '0';
        el.style.display = 'none';
      }
    });
  }

  async function refreshCartCount() {
    const cart = await fetchCart();
    if (cart) setCartCount(cart.item_count);
  }

  // refresh() is called by the sample pack and Saved-for-later scripts right after a successful
  // add, so it takes the same recovery path as the drawer's own changes. Resolves true or false.
  window.CCCart = {
    open: openCart,
    close: closeCart,
    refresh: function() { return syncCartAfterChange(); },
    refreshCount: function() { return refreshCartCount(); }
  };

  // Cart edits waiting to reach Shopify (2026-10-27, checkout audit). Checkout used to leave
  // the page while a typed quantity was still in its 200 ms wait, so checkout opened with the
  // old quantity; one shared timer also let a second row's edit cancel the first. Each line now
  // keeps its own pending number and its own chain of requests, so its answers cannot land out
  // of order, and every way out to checkout waits for all of them first.
  const qtyPending = {};    // line key -> { qty, timer }: typed, not yet sent
  const lineChains = {};    // line key -> the newest request chain for that line
  const lineWanted = {};    // line key -> the newest quantity sent or queued, until it settles
  const failedLines = new Set();  // lines whose saved quantity is not the one on screen
  const swaps = new Set();  // variant swaps in progress (they add and remove lines)
  let swapLeftBoth = false; // a swap ended with both paintings in the cart
  let cartPageFlash = null; // message to show after the deferred cart page reload
  let cartOpsInFlight = 0;
  let cardNoteSaving = 0;
  let cartPageReloadDue = false;
  let checkoutStarting = false;

  function cartSyncBusy() {
    return cartOpsInFlight > 0 || swaps.size > 0 || Object.keys(qtyPending).length > 0 || !!cardNote.timer || cardNoteSaving > 0;
  }

  // body.cart-is-syncing greys out the wallet buttons: they read the saved cart straight from
  // Shopify, so they must not start while an edit is still on its way.
  function updateCartSyncing() {
    document.body.classList.toggle('cart-is-syncing', cartSyncBusy());
  }

  // The cart page redraws by reloading. With several edits in flight it reloads once, after
  // the last, so a reload never cuts off another row's request or a note still saving.
  function maybeReloadCartPage() {
    // An unsaved note would be lost with the page; it saves (or shows its error) first.
    if (!cartPageReloadDue || checkoutStarting || cartSyncBusy() || cardNote.dirty) return;
    cartPageReloadDue = false;
    if (cartPageFlash) { const text = cartPageFlash; cartPageFlash = null; reloadCartPageWith(text); return; }
    location.reload();
  }

  const CART_FLASH = 'cc_cart_flash';
  function reloadCartPageWith(text) {
    try { sessionStorage.setItem(CART_FLASH, text); } catch (e) { /* message is lost, cart is still right */ }
    location.reload();
  }

  function queueLineUpdate(key, quantity, row, prevQty) {
    cartOpsInFlight++;
    updateCartSyncing();
    const before = lineChains[key] || Promise.resolve(true);
    const run = before
      .then(() => updateCartItem(key, quantity, row && row.isConnected ? row : null, prevQty))
      .catch(() => false);
    lineChains[key] = run;
    lineWanted[key] = quantity;
    return run.then(ok => {
      cartOpsInFlight--;
      if (lineChains[key] === run) {
        delete lineChains[key];
        delete lineWanted[key];
        if (ok) failedLines.delete(key); else failedLines.add(key);
      }
      updateCartSyncing();
      maybeReloadCartPage();
      return ok;
    });
  }

  function setQtyPending(item, qty, delay) {
    const key = item.dataset.key;
    const p = qtyPending[key] || (qtyPending[key] = { qty: qty, timer: null });
    clearTimeout(p.timer);
    p.qty = qty;
    p.timer = setTimeout(() => runQty(key), delay);
    updateCartSyncing();
  }

  function dropQtyPending(key) {
    const p = qtyPending[key];
    if (!p) return;
    clearTimeout(p.timer);
    delete qtyPending[key];
  }

  function runQty(key) {
    const p = qtyPending[key];
    if (!p) return Promise.resolve(true);
    dropQtyPending(key);
    return queueLineUpdate(key, p.qty, null);
  }

  // Sends every typed quantity now and waits for every line and the card note.
  // qty is true only when the saved cart holds each quantity on screen.
  async function flushCartEdits() {
    Object.keys(qtyPending).forEach(runQty);
    await Promise.all(Array.from(swaps));
    const lines = Object.keys(lineChains).map(k => lineChains[k]);
    const results = await Promise.all(lines);
    const note = await flushCardNote();
    return { qty: results.every(Boolean) && !failedLines.size && !swapLeftBoth, note: note };
  }

  // Card note (snippets/cc-card-note.liquid). The cart page and the drawer each draw a copy,
  // and either copy can be older than the saved cart: the audit typed a note on /cart, then
  // the drawer, drawn before the note existed, sent its blank copy at Checkout and erased it.
  // So the textarea has no name and never rides along in a form. The note is written only by
  // an edit made on this page, saved in order, mirrored into every copy, and saved before any
  // checkout leaves the page. A cleared note is an edit like any other and saves as empty.
  const cardNote = { value: null, dirty: false, failed: false, timer: null, chain: Promise.resolve(true) };

  function paintCardNote(except) {
    if (cardNote.value === null) return;
    document.querySelectorAll('[data-card-note]').forEach(box => {
      if (box !== except && box.value !== cardNote.value) box.value = cardNote.value;
      const wrap = box.closest('.cc-card-note');
      const count = wrap && wrap.querySelector('[data-card-note-count]');
      if (count) count.textContent = cardNote.value.length;
    });
    document.querySelectorAll('[data-wallet-note]').forEach(el => { el.hidden = !cardNote.value.trim(); });
  }

  function setCardNoteStatus(text, isError) {
    document.querySelectorAll('[data-card-note-status]').forEach(el => {
      el.textContent = text;
      el.hidden = !text;
      el.classList.toggle('is-error', !!isError);
    });
  }

  // Each save sends whatever the note says when its turn comes. Resolves true when the saved
  // cart holds the note on screen.
  function saveCardNote() {
    clearTimeout(cardNote.timer);
    cardNote.timer = null;
    cardNoteSaving++;
    updateCartSyncing();
    cardNote.chain = cardNote.chain.then(async () => {
      if (!cardNote.dirty) return true;
      const sending = cardNote.value;
      try {
        const r = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes: { 'Card note': sending } })
        });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        if (cardNote.value === sending) {
          cardNote.dirty = false;
          cardNote.failed = false;
          setCardNoteStatus('Note saved', false);
        }
        return !cardNote.dirty;
      } catch (e) {
        cardNote.failed = true;
        setCardNoteStatus('Your note is not saved yet. Check your connection. Checkout will save it.', true);
        return false;
      }
    });
    return cardNote.chain.then(ok => {
      cardNoteSaving--;
      updateCartSyncing();
      maybeReloadCartPage();
      return ok;
    });
  }

  async function flushCardNote() {
    for (let i = 0; i < 3 && (cardNote.dirty || cardNote.timer); i++) {
      if (!(await saveCardNote()) && cardNote.failed) return false;
    }
    await cardNote.chain;
    return !cardNote.dirty;
  }

  // A copy drawn earlier (another tab, or before the last save) takes the saved note, unless
  // this page holds an edit Shopify has not got yet.
  function adoptSavedNote(cart) {
    if (cardNote.dirty || cardNote.timer || !cart || !cart.attributes) return;
    cardNote.value = cart.attributes['Card note'] || '';
    paintCardNote(null);
  }

  document.addEventListener('input', e => {
    const box = e.target.closest && e.target.closest('[data-card-note]');
    if (!box) return;
    cardNote.value = box.value;
    cardNote.dirty = true;
    setCardNoteStatus('', false);
    paintCardNote(box);
    clearTimeout(cardNote.timer);
    cardNote.timer = setTimeout(saveCardNote, 500);
    updateCartSyncing();
  });

  function showCheckoutError(form, text) {
    let msg = form.querySelector('[data-checkout-error]');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'cart-line-error cart-checkout-error';
      msg.setAttribute('data-checkout-error', '');
      msg.setAttribute('role', 'alert');
      form.appendChild(msg);
    }
    msg.textContent = text;
  }

  // Every Checkout button (cart page and drawer) is a native POST of its form to /cart with
  // checkout set. Hold it until every edit made on this page has reached Shopify, then send it.
  // The line or lines behind a failed update are read back from Shopify and shown as saved, so
  // the shopper sees what checkout would get before trying again.
  async function recoverFailedLines() {
    if (!failedLines.size && !swapLeftBoth) return;
    const cart = await fetchCart();
    if (!cart) return;
    failedLines.clear();
    swapLeftBoth = false;
    if (isCartPage) { cartPageReloadDue = true; return; }
    reconcileCartDrawer(cart);
    setCartCount(cart.item_count);
  }

  const isCheckoutForm = form => {
    try { return new URL(form.action, location.href).pathname === '/cart' && !!form.querySelector('button[name="checkout"]'); }
    catch (err) { return false; }
  };
  let checkoutWatch = null;

  document.addEventListener('submit', async function(e) {
    let form = e.target;
    if (!form || !form.querySelector || !isCheckoutForm(form)) return;
    if (form.dataset.checkoutReady === '1') return;
    e.preventDefault();
    if (checkoutStarting) return;
    checkoutStarting = true;
    let btn = (e.submitter && e.submitter.name === 'checkout') ? e.submitter : form.querySelector('button[name="checkout"]');
    const inDrawer = !!btn.closest('[data-cart-drawer]');
    const old = form.querySelector('[data-checkout-error]');
    if (old) old.remove();
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    let result;
    try { result = await flushCartEdits(); } catch (err) { result = { qty: false, note: false }; }
    // A drawer redraw during the wait replaces its form; carry on with the new one.
    if (!form.isConnected) {
      const fresh = inDrawer && cartDrawer && cartDrawer.querySelector('form button[name="checkout"]');
      if (fresh) { form = fresh.form; btn = fresh; }
    }
    const release = () => {
      checkoutStarting = false;
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    };
    if (!result.qty) {
      release();
      const text = swapLeftBoth
        ? 'Both versions of a painting are in your cart. Remove the one you do not want, then check out.'
        : 'A quantity did not update, so checkout did not open. The cart now shows what is saved. Check it and try again.';
      await recoverFailedLines();
      if (isCartPage && cartPageReloadDue) { cartPageFlash = text; maybeReloadCartPage(); if (!cartPageReloadDue) return; }
      if (form.isConnected) showCheckoutError(form, text);
      else showCartDrawerNotice(text);
      return;
    }
    if (!form.isConnected) { release(); return; }
    // The note could not be saved ahead: send it with this checkout, so the edit made here
    // still lands (the /cart POST saves attributes before it opens checkout).
    if (!result.note && cardNote.dirty) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'attributes[Card note]';
      hidden.value = cardNote.value || '';
      hidden.setAttribute('data-note-fallback', '');
      form.appendChild(hidden);
    }
    form.dataset.checkoutReady = '1';
    // Next task: with nothing to wait for this runs inside the first submit's dispatch, when
    // the browser ignores a second submission of the same form.
    setTimeout(() => {
      if (form.requestSubmit) form.requestSubmit(btn);
      else {
        const flag = document.createElement('input');
        flag.type = 'hidden';
        flag.name = 'checkout';
        flag.value = '1';
        form.appendChild(flag);
        form.submit();
      }
    }, 0);
    // If the page is still here after 10 s the navigation never happened: take clicks again.
    clearTimeout(checkoutWatch);
    checkoutWatch = setTimeout(() => {
      delete form.dataset.checkoutReady;
      release();
    }, 10000);
  });

  window.addEventListener('pagehide', function() { clearTimeout(checkoutWatch); });

  // Back from checkout through the browser cache: the page must take the next click again, and
  // a cart page restored with edits it never redrew reloads to show the saved cart.
  window.addEventListener('pageshow', function(e) {
    checkoutStarting = false;
    document.querySelectorAll('form[data-checkout-ready]').forEach(f => { delete f.dataset.checkoutReady; });
    document.querySelectorAll('[data-note-fallback]').forEach(el => el.remove());
    document.querySelectorAll('[name="checkout"].is-loading').forEach(b => {
      b.classList.remove('is-loading');
      b.removeAttribute('aria-busy');
    });
    if (e.persisted && isCartPage && cartPageReloadDue) maybeReloadCartPage();
  });

  // Wallet buttons (Shop Pay, PayPal and the rest) cannot be held and resumed like the form,
  // so a tap while an edit is still saving is stopped, the edit is sent, and the shopper taps
  // again. CSS greys them out for the same window (body.cart-is-syncing).
  document.addEventListener('click', function(e) {
    const wallets = e.target.closest && e.target.closest('.cart-wallets');
    if (!wallets || !cartSyncBusy()) return;
    e.preventDefault();
    e.stopPropagation();
    flushCartEdits();
  }, true);

  if (isCartPage) {
    let flash = null;
    try { flash = sessionStorage.getItem(CART_FLASH); sessionStorage.removeItem(CART_FLASH); } catch (e) { /* none */ }
    const flashForm = flash && document.querySelector('.cart-page-summary form');
    if (flashForm) showCheckoutError(flashForm, flash);
  }

  // Opening the drawer shows the saved cart, not the one drawn at page load: lines and note
  // may have changed on the cart page or in another tab since.
  async function syncDrawerOnOpen() {
    if (!cartDrawer || cartSyncBusy()) return;
    const cart = await fetchCart();
    if (!cart || cartSyncBusy() || !cartDrawer.classList.contains('is-open')) return;
    const shown = Array.prototype.map.call(cartDrawer.querySelectorAll('[data-cart-item]'), r => r.dataset.key);
    const same = shown.length === cart.items.length && cart.items.every(l => shown.indexOf(l.key) > -1);
    if (same) {
      reconcileCartDrawer(cart);
    } else if (await refreshCartDrawer()) {
      const closeBtn = cartDrawer.querySelector('[data-cart-close]');
      if (closeBtn && !cartDrawer.contains(document.activeElement)) closeBtn.focus();
    }
    setCartCount(cart.item_count);
    adoptSavedNote(cart);
  }
  // Drawer cross-sell: replace the fallback collection tiles with Shopify's related
  // recommendations for the newest cart item. Runs on the parsed HTML before it is swapped in,
  // so the shopper never sees the fallback flash. Results are cached per product.
  const crossSellCache = {};
  function crossSellImg(src, w) {
    try { const u = new URL(src, location.origin); u.searchParams.set('width', w); return u.toString(); } catch (e) { return src; }
  }
  async function hydrateCrossSell(root) {
    const box = root.querySelector('[data-crosssell]');
    if (!box || !box.dataset.productId) return;
    const pid = box.dataset.productId;
    const exclude = (box.dataset.exclude || '').split(',').filter(Boolean);
    const count = parseInt(box.dataset.count, 10) || 3;
    try {
      if (!crossSellCache[pid]) {
        crossSellCache[pid] = fetch('/recommendations/products.json?product_id=' + pid + '&limit=10&intent=related')
          .then(r => r.ok ? r.json() : { products: [] })
          .then(j => j.products || [])
          .catch(() => { delete crossSellCache[pid]; return []; });
      }
      const picks = (await crossSellCache[pid]).filter(p => exclude.indexOf(String(p.id)) === -1).slice(0, count);
      if (picks.length < count) return;
      const grid = box.querySelector('.cart-drawer-crosssell-grid');
      if (!grid) return;
      const esc = t => String(t).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      grid.innerHTML = picks.map(p => {
        const name = p.title.split(' – ')[0].trim();
        const img = p.featured_image
          ? '<img src="' + esc(crossSellImg(p.featured_image, 160)) + '" srcset="' + [160, 240, 320, 480].map(w => esc(crossSellImg(p.featured_image, w)) + ' ' + w + 'w').join(', ') + '" sizes="(max-width: 749px) 31vw, 150px" alt="' + esc(p.title) + '" width="160" height="160" loading="lazy" decoding="async">'
          : '';
        return '<a href="' + esc(p.url) + '" class="cart-crosssell-tile" data-cart-close-soft>' + img +
          '<div class="cart-crosssell-info"><h4 class="cart-crosssell-title">' + esc(name) + '</h4></div>' +
          '<svg class="cart-crosssell-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg></a>';
      }).join('');
    } catch (e) { /* keep the fallback tiles */ }
  }
  hydrateCrossSell(document);

  // Redraws the drawer from Shopify's section HTML. Resolves true when it did, false when the
  // request failed or came back without drawer markup; it never throws. A false here says
  // nothing about the cart itself: callers have usually already saved a change, and must not
  // treat a failed redraw as a failed change.
  let drawerDrawSeq = 0;
  async function refreshCartDrawer() {
    const seq = ++drawerDrawSeq;
    try {
      const resp = await fetch('/?sections=cart-drawer');
      if (!resp.ok) return false;
      const json = await resp.json();
      const html = json && json['cart-drawer'];
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      await hydrateCrossSell(doc);
      const newDrawer = doc.querySelector('[data-cart-drawer]');
      const oldDrawer = document.querySelector('[data-cart-drawer]');
      if (!newDrawer || !oldDrawer) return false;
      // A newer redraw started while this one was loading; its HTML is the newer cart.
      if (seq !== drawerDrawSeq) return true;
      const typing = document.activeElement && oldDrawer.contains(document.activeElement) &&
        document.activeElement.matches('[data-card-note]') ? document.activeElement : null;
      const caret = typing ? [typing.selectionStart, typing.selectionEnd] : null;
      const wasOpen = oldDrawer.classList.contains('is-open');
      oldDrawer.innerHTML = newDrawer.innerHTML;
      if (wasOpen) oldDrawer.classList.add('is-open');
      // The new HTML is the saved cart; put back what this page has sent but not settled, then
      // what it has not sent yet, so the next + or - counts from the number on screen.
      const putBack = (key, qty) => {
        const input = oldDrawer.querySelector('[data-cart-item][data-key="' + key + '"] [data-qty-input]');
        if (input) input.value = qty;
      };
      Object.keys(lineWanted).forEach(key => putBack(key, lineWanted[key]));
      Object.keys(qtyPending).forEach(key => {
        putBack(key, qtyPending[key].qty);
        clearTimeout(qtyPending[key].timer);
        qtyPending[key].timer = setTimeout(() => runQty(key), 200);
      });
      if (cardNote.dirty) paintCardNote(null);
      if (typing) {
        const box = oldDrawer.querySelector('[data-card-note]');
        if (box) { box.focus(); if (caret) box.setSelectionRange(caret[0], caret[1]); }
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  // The saved cart from Shopify, or null when it cannot be read.
  async function fetchCart() {
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) return null;
      const cart = await r.json();
      return cart && Array.isArray(cart.items) ? cart : null;
    } catch (e) {
      return null;
    }
  }

  // Same output as Liquid's money_without_trailing_zeros for this USD store: $195, $1,234.50.
  function formatCartMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    return '$' + amount.toLocaleString('en-US', {
      minimumFractionDigits: amount % 1 ? 2 : 0,
      maximumFractionDigits: 2
    });
  }

  // When the redraw fails after Shopify saved a change, the rows on screen still show the old
  // cart. Bring them in line with the saved cart: quantities, line prices, subtotal, the count
  // in the title, and rows whose line is gone. Rows the drawer never had (a just-added item)
  // cannot be drawn without the section HTML; the notice below covers that.
  function reconcileCartDrawer(cart) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const lines = {};
    cart.items.forEach(l => { lines[l.key] = l; });
    drawer.querySelectorAll('[data-cart-item]').forEach(row => {
      const line = lines[row.dataset.key];
      if (!line) { row.remove(); return; }
      row.classList.remove('is-updating');
      const input = row.querySelector('[data-qty-input]');
      if (input) input.value = line.quantity;
      const price = row.querySelector('.cart-drawer-item-price');
      if (price && typeof line.final_line_price === 'number') price.textContent = formatCartMoney(line.final_line_price);
    });
    const subtotal = drawer.querySelector('[data-cart-subtotal]');
    if (subtotal && typeof cart.total_price === 'number') subtotal.textContent = formatCartMoney(cart.total_price);
    const count = drawer.querySelector('.cart-drawer-count');
    if (count) count.textContent = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';
  }

  // A visible, recoverable message at the top of the drawer. The button only re-reads the cart
  // (it never repeats the change); if the redraw still fails it reloads the page, which renders
  // the saved cart from the server.
  function showCartDrawerNotice(text) {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    let notice = drawer.querySelector('[data-cart-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'cart-drawer-notice';
      notice.setAttribute('data-cart-notice', '');
      notice.setAttribute('role', 'alert');
      const head = drawer.querySelector('.cart-drawer-head');
      if (head) head.insertAdjacentElement('afterend', notice);
      else drawer.insertBefore(notice, drawer.firstChild);
    }
    notice.innerHTML = '';
    const p = document.createElement('p');
    p.textContent = text;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cart-drawer-notice-btn';
    btn.setAttribute('data-cart-retry', '');
    btn.textContent = 'Refresh cart';
    notice.appendChild(p);
    notice.appendChild(btn);
  }

  document.addEventListener('click', async function(e) {
    const btn = e.target.closest('[data-cart-retry]');
    if (!btn) return;
    e.preventDefault();
    btn.disabled = true;
    btn.textContent = 'Refreshing...';
    if (await refreshCartDrawer()) {
      await refreshCartCount();
    } else {
      location.reload();
    }
  });

  // Run after any cart change Shopify accepted. `cart` is the saved cart when the caller already
  // has it (/cart/change.js answers with it). Redraws the drawer, or falls back to the saved
  // cart with a notice. Resolves true only for a full redraw; never throws, so a caller can
  // never mistake a display failure for a failed change and repeat it.
  async function syncCartAfterChange(cart) {
    try {
      if (await refreshCartDrawer()) {
        await refreshCartCount();
        return true;
      }
      const saved = (cart && Array.isArray(cart.items)) ? cart : await fetchCart();
      if (saved) {
        reconcileCartDrawer(saved);
        setCartCount(saved.item_count);
        showCartDrawerNotice('Your cart is saved, but part of it did not load. Refresh to see the latest.');
      } else {
        document.querySelectorAll('[data-cart-drawer] [data-cart-item].is-updating')
          .forEach(row => row.classList.remove('is-updating'));
        showCartDrawerNotice('Your cart was updated, but we could not load the latest version.');
      }
    } catch (e) {
      showCartDrawerNotice('Your cart was updated, but we could not load the latest version.');
    }
    return false;
  }

  // A failed cart update used to do nothing at all except log to a console no
  // shopper opens: they tap minus, the row does not move, and they conclude the
  // site is broken. That failure mode reaches us by phone call, not by metric.
  // Every path below either succeeds, or says so on screen and puts the row back.
  function showCartLineError(item, text) {
    if (!item) {
      window.alert('We could not update your cart. Please refresh the page and try again.');
      return;
    }
    let msg = item.querySelector('[data-cart-error]');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'cart-line-error';
      msg.setAttribute('data-cart-error', '');
      msg.setAttribute('role', 'alert');
      item.appendChild(msg);
    }
    msg.textContent = text || 'Could not update this item. Check your connection and try again.';
  }

  function clearCartLineError(item) {
    if (!item) return;
    const msg = item.querySelector('[data-cart-error]');
    if (msg) msg.remove();
  }

  // Resolves true when the saved cart holds `quantity` for this line, false otherwise. Called
  // through queueLineUpdate, never directly, so each line's requests stay in order.
  async function updateCartItem(key, quantity, row, prevQty) {
    const item = row || document.querySelector('[data-cart-item][data-key="' + key + '"]');
    clearCartLineError(item);
    // .is-updating was styled in theme.css but never applied by any code path,
    // so there was no busy affordance during the round trip at all.
    if (item) item.classList.add('is-updating');
    try {
      // The change and the redraw fail for different reasons and need different answers, so
      // they are kept apart. Only a change Shopify did not save puts the old number back.
      let r = null;
      try {
        r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
      } catch (netErr) {
        console.error('Cart update error:', netErr);
      }

      if (r && r.ok) {
        if (isCartPage) { cartPageReloadDue = true; return true; }
        // change.js answers with the saved cart; it is the fallback if the redraw fails.
        const cart = await r.json().catch(() => null);
        await syncCartAfterChange(cart);
        return true;
      }

      // Rejected, or the connection dropped and the outcome is unknown. Read what Shopify
      // actually saved before touching the number on screen; never repeat the change.
      const saved = await fetchCart();
      const line = saved && saved.items.find(l => l.key === key);
      const savedQty = saved ? (line ? line.quantity : 0) : null;
      if (savedQty === quantity) {
        // The change did save; only the answer was lost.
        if (isCartPage) { cartPageReloadDue = true; return true; }
        await syncCartAfterChange(saved);
        return true;
      }
      const input = item && item.querySelector('[data-qty-input]');
      if (input) {
        if (savedQty !== null) input.value = savedQty;
        else if (r && typeof prevQty === 'number') input.value = prevQty;
      }
      showCartLineError(item);
      return false;
    } finally {
      // Whatever happened, a row still on screen gets its controls back.
      if (item && item.isConnected) item.classList.remove('is-updating');
    }
  }

  // Swap a line item to a different variant (e.g. a different size or frame) without making the
  // shopper delete and re-find the product. The Cart AJAX API cannot change a line's variant in
  // place, so this adds the new variant, then zeroes the old line. Either step can fail or lose
  // its answer, and the old code reloaded on any error, which left BOTH paintings in the cart
  // with no word about it (2026-10-26 audit). Now every step is checked against the saved cart,
  // nothing is repeated, and a half-done swap is undone or explained. The new variant may
  // already have its own line; `base` is that quantity, and it survives whatever happens.
  function variantQty(cart, id) {
    return cart.items.reduce((n, l) => n + (l.variant_id === id ? l.quantity : 0), 0);
  }

  function changeCartVariant(item, newId, undoSelects) {
    if (item.dataset.swapping === '1') return Promise.resolve();
    item.dataset.swapping = '1';
    cartOpsInFlight++;
    updateCartSyncing();
    const run = swapCartVariant(item, newId, undoSelects).catch(() => {});
    swaps.add(run);
    return run.then(() => {
      swaps.delete(run);
      cartOpsInFlight--;
      updateCartSyncing();
      maybeReloadCartPage();
    });
  }

  async function swapCartVariant(item, newId, undoSelects) {
    const oldKey = item.dataset.key;
    clearCartLineError(item);
    item.classList.add('is-updating');
    const failNoChange = text => { undoSelects(); showCartLineError(item, text); };
    try {
      // A typed quantity for this row lands first, so the swap moves the number on screen.
      if (qtyPending[oldKey]) await runQty(oldKey);
      else if (lineChains[oldKey]) await lineChains[oldKey];
      const before = await fetchCart();
      if (!before) { failNoChange('Could not change this item. Check your connection and try again.'); return; }
      const oldLine = before.items.find(l => l.key === oldKey);
      if (!oldLine) {
        // Gone already (another tab). Show the saved cart rather than adding to it.
        if (isCartPage) { cartPageReloadDue = true; return; }
        await syncCartAfterChange(before);
        return;
      }
      const qty = oldLine.quantity;
      const base = variantQty(before, newId);

      let added = false;
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: newId, quantity: qty })
        });
        added = r.ok;
      } catch (e) { /* outcome unknown; read it below */ }
      if (!added) {
        const now = await fetchCart();
        if (!now) {
          showCartLineError(item, 'We could not confirm the change. Refresh your cart to see what was saved.');
          return;
        }
        if (variantQty(now, newId) < base + qty) {
          failNoChange('That option could not be added. Your cart is unchanged.');
          return;
        }
      }

      let removedCart = null;
      try {
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: oldKey, quantity: 0 })
        });
        if (r.ok) removedCart = (await r.json().catch(() => null)) || {};
      } catch (e) { /* outcome unknown; read it below */ }
      if (!removedCart) {
        const now = await fetchCart();
        if (now && !now.items.some(l => l.key === oldKey)) removedCart = now;
      }
      if (removedCart) {
        if (isCartPage) { cartPageReloadDue = true; return; }
        await syncCartAfterChange(Array.isArray(removedCart.items) ? removedCart : null);
        return;
      }

      // The new variant went in and the old line stayed. Take back only what this swap added.
      let undone = false;
      const now = await fetchCart();
      if (now) {
        const extra = Math.min(qty, variantQty(now, newId) - base);
        const lines = now.items.filter(l => l.variant_id === newId);
        const addedLine = lines.find(l => !l.properties || !Object.keys(l.properties).length) || lines[0];
        if (extra <= 0) {
          undone = true;
        } else if (addedLine) {
          try {
            const r = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: addedLine.key, quantity: Math.max(0, addedLine.quantity - extra) })
            });
            undone = r.ok;
          } catch (e) { /* checked below */ }
          if (!undone) {
            const check = await fetchCart();
            undone = !!check && variantQty(check, newId) <= base;
          }
        }
      }
      if (undone) {
        failNoChange('Could not change this item. Your cart is unchanged.');
        return;
      }
      const both = 'We added the new option but could not remove the old one, so both are in your cart. Remove the one you do not want.';
      swapLeftBoth = true;
      if (isCartPage) { cartPageReloadDue = true; cartPageFlash = both; return; }
      await syncCartAfterChange();
      showCartDrawerNotice(both);
    } finally {
      delete item.dataset.swapping;
      if (item.isConnected) {
        item.classList.remove('is-updating');
        item.querySelectorAll('[data-variant-size],[data-variant-frame]').forEach(el => { el.disabled = false; });
      }
    }
  }
  document.addEventListener('change', function(e) {
    const sel = e.target.closest('[data-variant-size],[data-variant-frame]');
    if (!sel) return;
    const item = sel.closest('[data-cart-item]');
    if (!item) return;
    const sizeSel = item.querySelector('[data-variant-size]');
    const frameSel = item.querySelector('[data-variant-frame]');
    const mapEl = item.querySelector('[data-variant-map]');
    if (!sizeSel || !frameSel || !mapEl) return;
    let variants;
    try { variants = JSON.parse(mapEl.textContent); } catch (_) { return; }
    const currentId = parseInt(item.dataset.variantId, 10);
    // A line keeps its frame style (Classic / Regency); two-option products have no o3.
    const curV = variants.find(v => v.id === currentId);
    const style = curV ? (curV.o3 || null) : null;
    const match = variants.find(v => v.o1 === sizeSel.value && v.o2 === frameSel.value && (v.o3 || null) === style);
    if (!match || !match.available) {
      // Combo doesn't exist / is sold out — snap the selects back.
      const cur = variants.find(v => v.id === currentId);
      if (cur) { sizeSel.value = cur.o1; frameSel.value = cur.o2; }
      return;
    }
    if (match.id === currentId) return;
    sizeSel.disabled = true;
    frameSel.disabled = true;
    changeCartVariant(item, match.id, () => {
      const cur = variants.find(v => v.id === currentId);
      if (cur) { sizeSel.value = cur.o1; frameSel.value = cur.o2; }
    });
  });

  document.addEventListener('click', function(e) {
    const decreaseBtn = e.target.closest('[data-qty-decrease]');
    const increaseBtn = e.target.closest('[data-qty-increase]');
    const removeBtn = e.target.closest('[data-cart-remove]');

    if (decreaseBtn) {
      e.preventDefault();
      const item = decreaseBtn.closest('[data-cart-item]');
      if (!item) return;
      const input = item.querySelector('[data-qty-input]');
      const key = item.dataset.key;
      const current = key in lineWanted ? lineWanted[key] : (parseInt(input ? input.value : '1', 10) || 1);
      const next = Math.max(0, current - 1);
      if (input) input.value = next;
      dropQtyPending(item.dataset.key);
      queueLineUpdate(item.dataset.key, next, item, current);
    } else if (increaseBtn) {
      e.preventDefault();
      const item = increaseBtn.closest('[data-cart-item]');
      if (!item) return;
      const input = item.querySelector('[data-qty-input]');
      const key = item.dataset.key;
      const current = key in lineWanted ? lineWanted[key] : (parseInt(input ? input.value : '0', 10) || 0);
      const next = current + 1;
      if (input) input.value = next;
      dropQtyPending(item.dataset.key);
      queueLineUpdate(item.dataset.key, next, item, current);
    } else if (removeBtn) {
      e.preventDefault();
      const item = removeBtn.closest('[data-cart-item]');
      if (!item) return;
      const input = item.querySelector('[data-qty-input]');
      const current = parseInt(input ? input.value : '1', 10) || 1;
      dropQtyPending(item.dataset.key);
      queueLineUpdate(item.dataset.key, 0, item, current);
    }
  });

  // A committed number (change: blur, Enter, spinner) is sent after 200 ms, per line.
  document.addEventListener('change', function(e) {
    if (!e.target.matches('[data-qty-input]')) return;
    const item = e.target.closest('[data-cart-item]');
    if (!item) return;
    const v = Math.max(0, parseInt(e.target.value || '0', 10) || 0);
    e.target.value = v;
    setQtyPending(item, v, 200);
  });
  // A number typed but not yet committed still counts if Checkout is pressed. An empty box is
  // someone retyping, not a removal, so it records nothing.
  document.addEventListener('input', function(e) {
    if (!e.target.matches || !e.target.matches('[data-qty-input]')) return;
    const item = e.target.closest('[data-cart-item]');
    const v = parseInt(e.target.value, 10);
    if (!item || e.target.value === '' || !(v >= 0)) return;
    // Sent on change (blur, Enter), at checkout, or after 1.5 s without a keystroke, so a box
    // replaced by a redraw before its change event can never leave the cart marked busy.
    setQtyPending(item, v, 1500);
  });

  // ===========================================================
  // Newsletter — submit to Klaviyo via klaviyo.js if loaded.
  // ===========================================================
  document.querySelectorAll('[data-newsletter-form]').forEach(form => {
    form.addEventListener('submit', function (e) {
      const emailInput = form.querySelector('input[name="email"]');
      const email = emailInput && emailInput.value && emailInput.value.trim();
      if (!email) return;

      if (typeof window._learnq !== 'undefined' || typeof window.klaviyo !== 'undefined') {
        e.preventDefault();
        try {
          var _learnq = window._learnq = window._learnq || [];
          _learnq.push(['identify', { '$email': email, '$source': form.dataset.source || 'newsletter' }]);
          _learnq.push(['track', 'Subscribed to Newsletter', {
            source: form.dataset.source || 'newsletter',
            list_id: form.dataset.klaviyoListId || null
          }]);
        } catch (err) { /* swallow */ }
        const labelEl = form.querySelector('.btn-label');
        const loadingEl = form.querySelector('.btn-loading');
        if (labelEl) labelEl.textContent = 'Subscribed ✓';
        if (loadingEl) loadingEl.style.display = 'none';
        const btn = form.querySelector('button[type="submit"]');
        if (btn) btn.disabled = true;
        if (emailInput) emailInput.disabled = true;
        form.classList.add('newsletter-success');
      }
    });
  });

  // ===========================================================
  // Predictive search overlay — the header search icon opens an
  // overlay with live product suggestions (/search/suggest.json)
  // plus the suggested-search chips. Keyed off body.search-is-open
  // (mirrors the nav/cart drawer pattern). Progressive enhancement:
  // the form still submits to /search and the chips are real links.
  // ===========================================================
  const psOverlay = document.querySelector('[data-search-overlay]');
  if (psOverlay) {
    const psBackdrop = document.querySelector('[data-search-backdrop]');
    const psInput = psOverlay.querySelector('[data-search-input]');
    const psResults = psOverlay.querySelector('[data-search-results]');
    let psLastTrigger = null;
    let psDebounce;
    let psReqId = 0;

    function openSearch(trigger) {
      psLastTrigger = trigger || document.querySelector('[data-search-open]');
      document.body.classList.add('search-is-open');
      psOverlay.setAttribute('aria-hidden', 'false');
      psOverlay.inert = false;
      if (psBackdrop) psBackdrop.setAttribute('aria-hidden', 'false');
      if (psLastTrigger) psLastTrigger.setAttribute('aria-expanded', 'true');
      setTimeout(function() { if (psInput) psInput.focus(); }, 60);
    }

    function closeSearch() {
      document.body.classList.remove('search-is-open');
      psOverlay.setAttribute('aria-hidden', 'true');
      psOverlay.inert = true;
      if (psBackdrop) psBackdrop.setAttribute('aria-hidden', 'true');
      document.querySelectorAll('[data-search-open]').forEach(function(t) { t.setAttribute('aria-expanded', 'false'); });
      if (psLastTrigger && typeof psLastTrigger.focus === 'function') psLastTrigger.focus();
    }

    document.addEventListener('click', function(e) {
      const openTrigger = e.target.closest('[data-search-open]');
      const closeTrigger = e.target.closest('[data-search-close]');
      if (openTrigger) {
        e.preventDefault();
        openSearch(openTrigger);
      } else if (closeTrigger) {
        e.preventDefault();
        closeSearch();
      }
    });

    if (psBackdrop) psBackdrop.addEventListener('click', closeSearch);

    // The overlay is a modal dialog: Escape closes it and Tab stays inside while it is open
    // (2026-10-26 audit: Tab walked out into the page behind it).
    document.addEventListener('keydown', function(e) {
      if (!document.body.classList.contains('search-is-open')) return;
      if (e.key === 'Escape') { closeSearch(); return; }
      if (e.key !== 'Tab') return;
      const items = Array.prototype.filter.call(
        psOverlay.querySelectorAll('a[href],summary,button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'),
        el => el.offsetWidth > 0 || el.offsetHeight > 0
      );
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (!psOverlay.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    function psFormatPrice(val) {
      const n = parseFloat(val);
      if (isNaN(n)) return '';
      return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
    }

    function psThumb(p) {
      const url = p.image || (p.featured_image && p.featured_image.url) || '';
      if (!url) return '';
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=120';
    }

    function renderResults(products, query) {
      psResults.innerHTML = '';
      if (!products.length) {
        const empty = document.createElement('p');
        empty.className = 'ps-empty';
        empty.textContent = 'No matches for "' + query + '" yet. Try another artist, mood, or room.';
        psResults.appendChild(empty);
        return;
      }
      // Shopify's suggest feed returns "related" products even when nothing matches the words
      // (e.g. "xylophone" -> Hassam, Waterhouse). If no result contains any typed word in its
      // title, artist, tags or type, say so instead of presenting the guesses as matches.
      const fold = function(t) { return String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); };
      const words = fold(query).split(/[^a-z0-9]+/).filter(function(w) { return w.length >= 3; });
      const hasMatch = !words.length || products.some(function(p) {
        const hay = fold([p.title, p.vendor, p.type, p.product_type, (p.tags || []).join(' ')].join(' '));
        // Word-start match only: "owl" must not count as found inside "knowledge".
        const hayWords = hay.split(/[^a-z0-9]+/);
        return words.some(function(w) {
          const stem = w.length > 4 ? w.replace(/s$/, '') : w;
          return hayWords.some(function(h) { return h.indexOf(stem) === 0; });
        });
      });
      if (!hasMatch) {
        const note = document.createElement('p');
        note.className = 'ps-suggest-note';
        note.textContent = 'No exact matches for "' + query + '". You might like:';
        psResults.appendChild(note);
      }
      products.forEach(function(p) {
        const a = document.createElement('a');
        a.className = 'ps-result';
        a.href = p.url;

        const thumb = psThumb(p);
        if (thumb) {
          const img = document.createElement('img');
          img.className = 'ps-result-thumb';
          img.src = thumb;
          img.alt = '';
          img.loading = 'lazy';
          a.appendChild(img);
        }

        const text = document.createElement('div');
        text.className = 'ps-result-text';
        const parts = (p.title || '').split(' – ');
        const title = document.createElement('span');
        title.className = 'ps-result-title';
        title.textContent = parts[0] || p.title || '';
        text.appendChild(title);
        if (parts[1]) {
          const artist = document.createElement('span');
          artist.className = 'ps-result-artist';
          artist.textContent = (parts[1].split(', ')[0] || '').trim();
          text.appendChild(artist);
        }
        a.appendChild(text);

        const price = psFormatPrice(p.price_min || p.price);
        if (price) {
          const pr = document.createElement('span');
          pr.className = 'ps-result-price';
          pr.textContent = 'From ' + price;
          a.appendChild(pr);
        }
        psResults.appendChild(a);
      });

      const viewAll = document.createElement('a');
      viewAll.className = 'ps-viewall';
      viewAll.href = '/search?q=' + encodeURIComponent(window.ccScopeSearchQuery ? window.ccScopeSearchQuery(query) : query) + '&qd=' + encodeURIComponent(query) + '&type=product';
      viewAll.textContent = 'View all results for "' + query + '"';
      psResults.appendChild(viewAll);
    }

    async function fetchSuggestions(query) {
      const reqId = ++psReqId;
      try {
        const resp = await fetch('/search/suggest.json?q=' + encodeURIComponent(window.ccCanonQuery ? window.ccCanonQuery(query) : query) +
          '&resources[type]=product&resources[limit]=6' +
          '&resources[options][fields]=title,tag,vendor,product_type');
        if (!resp.ok) return;
        const data = await resp.json();
        if (reqId !== psReqId) return; // a newer keystroke superseded this one
        const products = (data.resources && data.resources.results && data.resources.results.products) || [];
        renderResults(products, query);
      } catch (err) { /* ignore network errors — form submit still works */ }
    }

    if (psInput) {
      psInput.addEventListener('input', function() {
        const q = psInput.value.trim();
        clearTimeout(psDebounce);
        if (q.length < 2) {
          psReqId++; // cancel any pending render
          psResults.innerHTML = '';
          return;
        }
        psDebounce = setTimeout(function() { fetchSuggestions(q); }, 200);
      });
    }
  }
})();

/* Announcement bar rotator — mobile only.
   Crossfades through the announce-item messages so all three show on small
   screens (where there's only room for one). Desktop keeps showing them all
   statically. Respects prefers-reduced-motion (static first message) and
   pauses advancing while the tab is hidden. */
(function () {
  var bar = document.querySelector('.announce');
  if (!bar) return;
  var items = bar.querySelectorAll('.announce-item');
  if (items.length < 2) return;

  var mqMobile = window.matchMedia('(max-width: 768px)');
  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var idx = 0, timer = null;

  function show(i) {
    for (var n = 0; n < items.length; n++) {
      items[n].classList.toggle('is-active', n === i);
    }
  }
  function start() {
    if (timer) return;
    bar.classList.add('is-rotating');
    idx = 0; show(0);
    timer = setInterval(function () {
      if (document.hidden) return;            // pause advancing while tab is hidden
      idx = (idx + 1) % items.length;
      show(idx);
    }, 4000);
  }
  function stop() {
    if (timer) { clearInterval(timer); timer = null; }
    bar.classList.remove('is-rotating');
    for (var n = 0; n < items.length; n++) items[n].classList.remove('is-active');
  }
  // Phones now show the first message, still (2026-10-20): an endless 4 s rotation with no pause
  // fails WCAG 2.2.2 and older shoppers track moving text poorly. start() is kept for reuse.
  function apply() {
    stop();
  }

  apply();
  function on(mq, fn) { mq.addEventListener ? mq.addEventListener('change', fn) : mq.addListener(fn); }
  on(mqMobile, apply);
  on(mqReduce, apply);
})();

/* The Craft — mobile "tap for more" popup.
   On phones the grid is a compact 2x2 of number + title; tapping a card opens
   the popup with that step's full description. Desktop shows everything inline,
   so the popup stays dormant there. */
(function () {
  var grid = document.querySelector('.craft-grid');
  var popup = document.getElementById('craftPopup');
  if (!grid || !popup) return;

  var mq = window.matchMedia('(max-width:560px)');
  var numEl = popup.querySelector('.craft-popup-num');
  var titleEl = popup.querySelector('.craft-popup-title');
  var bodyEl = popup.querySelector('.craft-popup-body');
  var closeBtn = popup.querySelector('.craft-popup-close');
  var lastFocus = null;
  var hideTimer = null;

  function text(el, sel) { var n = el.querySelector(sel); return n ? n.textContent.trim() : ''; }

  function open(step) {
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    numEl.textContent = text(step, '.craft-num');
    titleEl.textContent = text(step, 'h3');
    bodyEl.textContent = text(step, 'p');
    popup.classList.toggle('is-signature', step.classList.contains('signature'));
    lastFocus = document.activeElement;
    popup.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(function () { popup.classList.add('is-open'); });
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    popup.classList.remove('is-open');
    document.body.style.overflow = '';
    hideTimer = setTimeout(function () { popup.hidden = true; }, 320);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  grid.addEventListener('click', function (e) {
    if (!mq.matches) return;                     // desktop shows full text inline
    var step = e.target.closest('.craft-step');
    if (step) open(step);
  });
  popup.addEventListener('click', function (e) {
    if (e.target.hasAttribute('data-craft-close')) close();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !popup.hidden) close();
  });
})();

/* Collection intro: hide the Read more / fade when the bio fits inside the clamp.
   Measures the clamped description; if it doesn't overflow, mark it .intro-fits
   (CSS then drops the clamp, fade, and toggle). Re-checks on resize. */
(function () {
  var intros = document.querySelectorAll('[data-collection-intro]');
  if (!intros.length) return;

  function check(intro) {
    var desc = intro.querySelector('.collection-description');
    var cb = intro.querySelector('.collection-desc-cb');
    if (!desc) return;
    // Only meaningful while collapsed — skip if the user has expanded it.
    if (cb && cb.checked) return;
    intro.classList.remove('intro-fits');
    var overflows = desc.scrollHeight - desc.clientHeight > 4;
    if (!overflows) intro.classList.add('intro-fits');
  }

  function checkAll() {
    intros.forEach(check);
  }

  checkAll();
  // Recompute after web fonts settle (line height can shift) and on resize.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(checkAll);
  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(checkAll, 150);
  });
})();

/* PDP description: drop the clamp/fade/toggle when the copy already fits inside the clamp.
   Same approach as the collection intro, keyed to a separate selector so the two never
   collide. Bails immediately on pages with no PDP description block. */
(function () {
  var blocks = document.querySelectorAll('[data-pdp-desc]');
  if (!blocks.length) return;

  function check(b) {
    var body = b.querySelector('.pdp-desc-body');
    if (!body) return;
    // Only meaningful while collapsed; skip if the user has expanded it.
    if (b.classList.contains('is-open')) return;
    b.classList.remove('desc-fits');
    if (body.scrollHeight - body.clientHeight <= 4) b.classList.add('desc-fits');
  }

  function checkAll() {
    blocks.forEach(check);
  }

  blocks.forEach(function (b) {
    var btn = b.querySelector('[data-pdp-desc-toggle]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var open = b.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });

  checkAll();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(checkAll);
  var t;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(checkAll, 150);
  });
})();

/* Storewide reviews blocks (PDP pooled block + homepage carousel): reveal only once Junip
   actually fills the carousel, otherwise keep it hidden so an empty widget never leaves a
   blank gap. Covers .pdp-reviews-shop and any [data-junip-reveal] container. */
(function () {
  var blocks = document.querySelectorAll('.pdp-reviews-shop, [data-junip-reveal]');
  if (!blocks.length) return;
  blocks.forEach(function (block) {
    function hasContent(sel) {
      var el = block.querySelector(sel);
      return el && el.querySelector('*') && el.textContent.trim().length > 0;
    }
    function check() {
      if (hasContent('.junip-review-carousel')) {
        block.classList.add('is-populated');
        return true;
      }
      return false;
    }
    if (check()) return;
    var obs = new MutationObserver(function () { if (check()) obs.disconnect(); });
    obs.observe(block, { childList: true, subtree: true });
    // Give Junip time to load; if it never fills, the block stays hidden.
    setTimeout(function () { obs.disconnect(); }, 10000);
  });
})();

/* Search query scoping. Shopify storefront search matches product DESCRIPTIONS too, and
   our descriptions name-drop other artists ("much like Matisse..."), so "matisse" returned
   234 results instead of 54. Field-scoped queries fix that: every word becomes
   (title:word* OR tag:word*), so results match on title and tags only.
   Mirrors snippets/search-scoped-query.liquid. Keep the two in sync.
   Also lowercases q: Shopify's typo-correction only works on lowercase input. */
(function () {
  // Prefix matching means the singular stem finds the plural too ("nude*" hits "nudes"),
  // but not the reverse, so strip plural endings before adding the wildcard.
  // Shopify returns nothing for a wildcard under four letters ("lil*" found 0 where "lili*"
  // finds 7, 2026-10-27), so a stem shorter than that keeps the word as typed.
  function stem(w) {
    if (w.length <= 3 || !/s$/.test(w) || /ss$/.test(w)) return w;
    var out;
    if (/ies$/.test(w)) out = w.slice(0, -3);              // ponies -> pon* (lilies stays)
    else if (/(ses|xes|zes|ches|shes)$/.test(w)) out = w.slice(0, -2); // horses -> hors*
    else out = w.slice(0, -1);                            // portraits -> portrait*
    return out.length < 4 ? w : out;
  }
  // Search synonyms. Shopify retired Search & Discovery synonyms (Sept 2026), so whole-query
  // rewrites live here: misspellings and alternate words searched in the last year that found
  // nothing, mapped to the word our titles and tags use. Only an exact whole query is swapped.
  var SYN = {
    'vangogh': 'van gogh', 'van goh': 'van gogh', 'van gough': 'van gogh', 'vangoh': 'van gogh', 'van go': 'van gogh',
    'klimpt': 'klimt', 'klimit': 'klimt',
    'dark academa': 'dark academia',
    'rembrant': 'rembrandt',
    'napolean': 'napoleon',
    'pissaro': 'pissarro', 'pisarro': 'pissarro', 'camille pissaro': 'pissarro',
    'hassan': 'hassam', 'childe hassan': 'hassam',
    'davinci': 'da vinci',
    'pierre augusta renoir': 'renoir', 'renior': 'renoir',
    'irisis': 'irises',
    'water lillies': 'water lilies',
    'book worm': 'bookworm',
    'jesus': 'christ', 'jesus christ': 'christ',
    'religion': 'religious', 'religous': 'religious',
    'sea scapes': 'seascape', 'sailing and sea scapes': 'seascape',
    'germany': 'german',
    'florals': 'floral',
    'native americans': 'native american',
    'frederick church': 'frederic edwin church',
    'frederick remington': 'frederic remington',
    'fox hunt': 'hunting', 'fox hunting': 'hunting',
    'tuscany': 'italy', 'florence': 'italy',
    'japan': 'japanese'
  };
  function canon(raw) {
    var k = String(raw || '').toLowerCase().trim().replace(/\s+/g, ' ');
    return SYN.hasOwnProperty(k) ? SYN[k] : raw;
  }
  function scope(raw) {
    raw = canon(raw);
    var words = String(raw || '').toLowerCase().trim().split(/\s+/);
    var out = [], clean = [];
    for (var i = 0; i < words.length; i++) {
      var t = stem(words[i].replace(/["():*]/g, ''));
      if (t) { clean.push(t); out.push('(title:' + t + '* OR tag:' + t + '*)'); }
    }
    if (out.length < 2) return out.join(' ');
    // Multi-word: also try the words run together, so "cottage core" finds tag "cottagecore".
    var joined = clean.join('');
    return '(' + out.join(' ') + ') OR (title:' + joined + '* OR tag:' + joined + '*)';
  }
  window.ccScopeSearchQuery = scope;
  window.ccCanonQuery = canon;

  // A plain /search?q=vangogh link (old bookmark, outside link) gets the same rewrite.
  if (location.pathname === '/search') {
    var sp = new URLSearchParams(location.search), rawQ = sp.get('q') || '';
    // ccp=1 marks the plain-search fallback below; rewriting it again would bounce forever.
    if (rawQ && !sp.has('ccp') && !/title:/.test(rawQ) && canon(rawQ) !== rawQ) {
      sp.set('qd', rawQ); sp.set('q', scope(rawQ));
      location.replace('/search?' + sp.toString());
    }
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.action) return;
    var path;
    try { path = new URL(form.action, location.origin).pathname; } catch (err) { return; }
    if (path !== '/search') return;
    var q = form.querySelector('input[name="q"]');
    if (!q || !q.value) return;
    if (/title:/.test(q.value) && /tag:/.test(q.value)) return; // already scoped (filter form)
    var typed = q.value.trim();
    q.value = scope(typed);
    // Carry what the shopper actually typed so the results page can echo it back.
    var qd = form.querySelector('input[name="qd"]');
    if (!qd) { qd = document.createElement('input'); qd.type = 'hidden'; qd.name = 'qd'; form.appendChild(qd); }
    qd.value = typed;
  }, true);

  // Results page: echo the typed words (not the stemmed query). When the scoped search finds
  // nothing, the page stays a truthful "no results" page. It used to redirect to Shopify's
  // plain search, which answered a nonsense word with "1000 results" of unrelated best sellers
  // (2026-10-26 audit). Typos still get help: predictive search (typo tolerant, and empty for
  // nonsense) fills a block clearly labelled as close matches, plus its suggested searches.
  if (location.pathname === '/search') {
    var params = new URLSearchParams(location.search);
    var typedQ = params.get('qd');
    var scopedQ = params.get('q') || '';
    var isScoped = /title:/.test(scopedQ) && /tag:/.test(scopedQ);
    if (typedQ && isScoped) {
      document.querySelectorAll('[data-search-terms]').forEach(function (el) { el.textContent = typedQ; });
      document.querySelectorAll('[data-search-terms-input]').forEach(function (el) { el.value = typedQ; });
    }
    var closeBox = document.querySelector('[data-search-close-matches]');
    var hasFilters = Array.from(params.keys()).some(function (k) { return k.indexOf('filter.') === 0; });
    var asked = typedQ || (isScoped ? '' : scopedQ);
    if (closeBox && asked && !hasFilters && window.fetch) showCloseMatches(closeBox, asked, isScoped);
  }

  // Word-start match of any typed word (3+ letters) in a suggestion, the same test the header
  // overlay uses: "owl" must not count as found inside "knowledge".
  function suggestionMatches(products, asked) {
    var fold = function (t) { return String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); };
    var words = fold(asked).split(/[^a-z0-9]+/).filter(function (w) { return w.length >= 3; });
    if (!words.length) return false;
    return products.some(function (p) {
      var hay = fold([p.title, p.vendor, p.type, (p.tags || []).join(' ')].join(' ')).split(/[^a-z0-9]+/);
      return words.some(function (w) {
        var st = w.length > 4 ? w.replace(/s$/, '') : w;
        return hay.some(function (h) { return h.indexOf(st) === 0; });
      });
    });
  }

  function showCloseMatches(box, asked, fromScoped) {
    var esc = function (t) { return String(t).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };
    var url = '/search/suggest.json?q=' + encodeURIComponent(asked) +
      '&resources[type]=product,query&resources[limit]=8&resources[options][unavailable_products]=hide';
    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var res = d && d.resources && d.resources.results;
        if (!res) return;
        var typed = asked.toLowerCase().trim();
        var queries = (res.queries || []).map(function (q) { return q.text; })
          .filter(function (t) { return t && t.toLowerCase().trim() !== typed; }).slice(0, 3);
        var products = res.products || [];
        // Real words the field-scoped query missed (it found 0 for "water lilies"): Shopify's
        // plain search finds them, as it did before. Only nonsense and typos stay here.
        if (fromScoped && products.length && suggestionMatches(products, asked)) {
          location.replace('/search?q=' + encodeURIComponent(asked) + '&type=product&ccp=1');
          return;
        }
        if (!products.length && !queries.length) return;
        var html = '';
        if (queries.length) {
          html += '<p class="search-close-did">Did you mean ' + queries.map(function (t) {
            return '<a href="/search?q=' + encodeURIComponent(scope(t)) + '&qd=' + encodeURIComponent(t) + '&type=product">' + esc(t) + '</a>';
          }).join(', ') + '?</p>';
        }
        if (products.length) {
          html += '<h3 class="search-close-heading">Close matches, not exact</h3><ul class="search-close-list">' +
            products.map(function (p) {
              var img = p.featured_image && p.featured_image.url
                ? '<img src="' + esc(p.featured_image.url + (p.featured_image.url.indexOf('?') > -1 ? '&' : '?') + 'width=240') + '" alt="" width="120" height="120" loading="lazy">' : '';
              return '<li><a href="' + esc(p.url) + '">' + img + '<span>' + esc(p.title) + '</span></a></li>';
            }).join('') + '</ul>';
        }
        box.innerHTML = html;
        box.hidden = false;
      })
      .catch(function () { /* the plain empty state stays */ });
  }
})();

/* Drawn frames on Safari, and anywhere without container queries (2026-10-08). The frame box
   sizes itself in cqw/cqh units. Safari before 16.0 has no such units, so on iOS 15 every size
   rule was dropped and the frame drew about 13 px wide. Safari 16 and 17 have them but kept a
   stale cq-based border-width after a size or finish change, drawing Regency with Classic's
   4 in moulding and squeezing tall paintings to half width. So on WebKit (every iOS browser
   reports vendor Apple) and on browsers without the units, the same boxes are sized in px
   from their container with the same formula theme.css uses. Chrome, Edge and Firefox keep
   the pure CSS path. */
(function () {
  var hasCq = window.CSS && CSS.supports && CSS.supports('width', '1cqw');
  if (hasCq && !/Apple/.test(navigator.vendor || '')) return;
  var SEL = '[data-css-frame-box], img.cart-drawer-item-framed';
  function num(v, d) { var n = parseFloat(v); return isFinite(n) && n > 0 ? n : d; }
  function size(el) {
    var par = el.parentElement;
    if (!par) return;
    var pcs = getComputedStyle(par), cs = getComputedStyle(el);
    var cw = par.clientWidth - num(pcs.paddingLeft, 0) - num(pcs.paddingRight, 0);
    var ch = par.clientHeight - num(pcs.paddingTop, 0) - num(pcs.paddingBottom, 0);
    if (cw <= 0 || ch <= 0) return;
    var fw = num(cs.getPropertyValue('--fw'), 30), fh = num(cs.getPropertyValue('--fh'), 24);
    var face = el.matches('img.cart-drawer-item-framed') ? 4 : num(cs.getPropertyValue('--face-in'), 4);
    var w = Math.min(cw, ch * fw / fh), h = w * fh / fw;
    // Only write on a real change: these writes are style mutations the observer below sees.
    var sw = w.toFixed(2) + 'px', sh = h.toFixed(2) + 'px', sb = (w * face / fw).toFixed(2) + 'px';
    if (el.style.width !== sw) el.style.width = sw;
    if (el.style.height !== sh) el.style.height = sh;
    if (el.style.borderWidth !== sb) el.style.borderWidth = sb;
  }
  function all() { document.querySelectorAll(SEL).forEach(size); }
  var queued = false;
  function soon() { if (queued) return; queued = true; requestAnimationFrame(function () { queued = false; all(); }); }
  // --fw/--fh change per size click, data-frame per finish (Regency's face is thinner), and the
  // lightbox and cart drawer build or reveal boxes on demand, so watch the whole document.
  new MutationObserver(function (list) {
    for (var i = 0; i < list.length; i++) {
      var t = list[i].target;
      if (list[i].type === 'childList' || (t.matches && (t.matches(SEL) || t.matches('[data-frame], .gallery-lightbox, .cart-drawer, body')))) { soon(); return; }
    }
  }).observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'data-frame', 'class', 'hidden'] });
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(soon), seen = [];
    var watch = function () {
      document.querySelectorAll(SEL).forEach(function (el) {
        var p = el.parentElement;
        if (p && seen.indexOf(p) < 0) { seen.push(p); ro.observe(p); }
      });
    };
    var all0 = all;
    all = function () { watch(); all0(); };
  }
  window.addEventListener('resize', soon);
  window.addEventListener('orientationchange', soon);
  window.addEventListener('load', soon);
  if (document.readyState !== 'loading') all(); else document.addEventListener('DOMContentLoaded', all);
})();
